import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import DailyPnLCard from '../components/DailyPnLCard';
import VerifiedOperationHistory from '../components/VerifiedOperationHistory';
import BuybackCard from '../components/BuybackCard';
import DryRunCard from '../components/DryRunCard';
import BounceCard from '../components/BounceCard';
import DexHealthCard from '../components/DexHealthCard';
import ToxicFlowCard from '../components/ToxicFlowCard';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  Play,
  Pause,
  Settings,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Activity,
  BarChart3,
  Clock,
  Target,
  ExternalLink,
  Plus,
  Edit,
  Trash2,
  Users,
  ShieldAlert,
  Power,
  Zap,
  Anchor,
  Grid3x3
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

import { getApiUrl } from '../utils/api';
import DumpBuyback from '../components/DumpBuyback';
import { useTokenConfig } from '../contexts/TokenConfigContext';

const API_URL = getApiUrl();
const LOG_FETCH_LIMIT = 300;

// AUDIT 2026-07-02: записи jetton_balances имеют вид
// {symbol, balance:"<nano-строка>", decimals}, ключ — ПОЛНЫЙ адрес токена (EQ...);
// у старых кошельков значение может быть скаляром-нано. Раньше UI делал `entry / 1e9`
// (объект → NaN) и мог искать по символу → балансы токенов рендерились как 0/NaN, а
// «выбрать кошельки с токенами» (сравнение объекта `> 0`) не выбирал ничего.
// Возвращает human-readable float, устойчив к обеим формам и к промаху ключа.
const jettonReadable = (jettonBalances, tokenAddr, fallbackAddr) => {
  if (!jettonBalances) return 0;
  let entry = jettonBalances[tokenAddr];
  if (entry == null && fallbackAddr && fallbackAddr !== tokenAddr) {
    entry = jettonBalances[fallbackAddr];
  }
  if (entry == null) return 0;
  if (typeof entry === 'object') {
    const bal = Number(entry.balance ?? entry.amount ?? 0);
    const dec = Number(entry.decimals ?? 9);
    if (!isFinite(bal) || !isFinite(dec)) return 0;
    return bal / Math.pow(10, dec);
  }
  const n = Number(entry);
  return isFinite(n) ? n / 1e9 : 0;
};

// Компонент истории Random Pulse
const RandomPulseHistory = () => {
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [selectedSeries, setSelectedSeries] = useState(null);
  const limit = 10;

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Используем полный путь с /api
        const baseUrl = `${API_URL}/api`;
        const [historyRes, statsRes] = await Promise.all([
          axios.get(`${baseUrl}/market-maker/random-history`, {
            params: { limit, offset: page * limit }
          }),
          axios.get(`${baseUrl}/market-maker/random-stats`)
        ]);
        
        if (historyRes.data.success) {
          setHistory(historyRes.data.history || []);
          setTotal(historyRes.data.total || 0);
        }
        if (statsRes.data.success) {
          setStats(statsRes.data.stats);
        }
      } catch (e) {
        console.error('Error loading random data:', e);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
    // Авто-обновление каждые 10 секунд
    const interval = setInterval(loadData, 10000);
    return () => clearInterval(interval);
  }, [page]);

  // Подсчёт объёмов по направлениям для выбранной серии
  const getSeriesVolumes = (series) => {
    const steps = series.executed_steps || series.planned_steps || [];
    const buyVolume = steps.filter(s => s.direction === 'buy').reduce((sum, s) => sum + (s.volume_usd || 0), 0);
    const sellVolume = steps.filter(s => s.direction === 'sell').reduce((sum, s) => sum + (s.volume_usd || 0), 0);
    return { buyVolume, sellVolume };
  };

  // Расчёт изменения цены в процентах
  const getPriceChange = (series) => {
    if (!series.start_price_usd || !series.end_price_usd || series.start_price_usd === 0) return null;
    return ((series.end_price_usd - series.start_price_usd) / series.start_price_usd * 100);
  };

  if (loading && history.length === 0) {
    return <div className="text-slate-500 text-sm">Загрузка истории...</div>;
  }

  return (
    <div className="space-y-4">
      {/* Статистика */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="bg-slate-100 dark:bg-slate-800 rounded p-3 border border-slate-200 dark:border-slate-700">
            <div className="text-xs text-slate-600 dark:text-slate-400">Всего серий</div>
            <div className="text-lg font-medium text-slate-900 dark:text-white">{stats.overall?.total_series || 0}</div>
          </div>
          <div className="bg-slate-100 dark:bg-slate-800 rounded p-3 border border-slate-200 dark:border-slate-700">
            <div className="text-xs text-slate-600 dark:text-slate-400">Успешных</div>
            <div className="text-lg font-medium text-emerald-600 dark:text-emerald-400">
              {stats.overall?.completed || 0} ({(stats.overall?.success_rate || 0).toFixed(0)}%)
            </div>
          </div>
          <div className="bg-slate-100 dark:bg-slate-800 rounded p-3 border border-slate-200 dark:border-slate-700">
            <div className="text-xs text-slate-600 dark:text-slate-400">Общий объём</div>
            <div className="text-lg font-medium text-slate-900 dark:text-white">${(stats.overall?.total_volume_usd || 0).toFixed(2)}</div>
          </div>
          <div className="bg-slate-100 dark:bg-slate-800 rounded p-3 border border-slate-200 dark:border-slate-700">
            <div className="text-xs text-slate-600 dark:text-slate-400">Сегодня</div>
            <div className="text-lg font-medium text-blue-600 dark:text-blue-400">
              {stats.today?.series_count || 0} серий / ${(stats.today?.volume_usd || 0).toFixed(2)}
            </div>
          </div>
        </div>
      )}

      {/* Таблица истории */}
      {history.length === 0 ? (
        <div className="text-slate-600 dark:text-slate-400 text-sm text-center py-6">История пуста</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-600 dark:text-slate-400 border-b border-slate-300 dark:border-slate-700">
                <th className="py-2 px-2">Время</th>
                <th className="py-2 px-2">Шаблон</th>
                <th className="py-2 px-2">Статус</th>
                <th className="py-2 px-2">Шаги</th>
                <th className="py-2 px-2">Объём</th>
                <th className="py-2 px-2">Цена</th>
              </tr>
            </thead>
            <tbody>
              {history.map((item) => (
                <tr 
                  key={item.series_id} 
                  className="border-b border-slate-200 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800/50 cursor-pointer"
                  onClick={() => setSelectedSeries(item)}
                >
                  <td className="py-2 px-2 text-slate-700 dark:text-slate-300">
                    {item.started_at ? new Date(item.started_at).toLocaleString('ru-RU', {
                      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
                    }) : '—'}
                  </td>
                  <td className="py-2 px-2 text-slate-900 dark:text-white font-medium">{item.template_name || '—'}</td>
                  <td className="py-2 px-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      item.status === 'completed' ? 'bg-emerald-600 text-white' :
                      item.status === 'aborted' ? 'bg-orange-600 text-white' :
                      item.status === 'failed' ? 'bg-red-600 text-white' :
                      'bg-slate-500 text-white'
                    }`}>
                      {item.status}
                    </span>
                  </td>
                  <td className="py-2 px-2 text-slate-700 dark:text-slate-300">
                    {item.executed_count || 0}/{item.total_steps || 0}
                    <span className="text-xs text-slate-500 ml-1">
                      ({item.successes || 0}✓ {item.failures || 0}✗)
                    </span>
                  </td>
                  <td className="py-2 px-2 text-slate-700 dark:text-slate-300 font-medium">${(item.total_volume_usd || 0).toFixed(2)}</td>
                  <td className="py-2 px-2 text-xs">
                    {item.start_price_usd && item.end_price_usd ? (
                      <span className={item.end_price_usd >= item.start_price_usd ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}>
                        ${item.start_price_usd.toFixed(8)} → ${item.end_price_usd.toFixed(8)}
                      </span>
                    ) : <span className="text-slate-500">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Модальное окно деталей серии */}
      {selectedSeries && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setSelectedSeries(null)}>
          <div 
            className="bg-white dark:bg-slate-900 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
              <div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                  {selectedSeries.template_name}
                </h3>
                <p className="text-sm text-slate-500">{selectedSeries.series_id}</p>
              </div>
              <button 
                onClick={() => setSelectedSeries(null)}
                className="text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Сводка */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-slate-100 dark:bg-slate-800 rounded p-3">
                  <div className="text-xs text-slate-600 dark:text-slate-400">Статус</div>
                  <div className={`font-bold ${
                    selectedSeries.status === 'completed' ? 'text-emerald-600' :
                    selectedSeries.status === 'failed' ? 'text-red-600' : 'text-orange-600'
                  }`}>{selectedSeries.status}</div>
                </div>
                <div className="bg-emerald-100 dark:bg-emerald-900/30 rounded p-3 border border-emerald-300 dark:border-emerald-700">
                  <div className="text-xs text-emerald-700 dark:text-emerald-400">Покупки</div>
                  <div className="font-bold text-emerald-700 dark:text-emerald-300">
                    ${getSeriesVolumes(selectedSeries).buyVolume.toFixed(2)}
                  </div>
                </div>
                <div className="bg-red-100 dark:bg-red-900/30 rounded p-3 border border-red-300 dark:border-red-700">
                  <div className="text-xs text-red-700 dark:text-red-400">Продажи</div>
                  <div className="font-bold text-red-700 dark:text-red-300">
                    ${getSeriesVolumes(selectedSeries).sellVolume.toFixed(2)}
                  </div>
                </div>
                <div className="bg-slate-100 dark:bg-slate-800 rounded p-3">
                  <div className="text-xs text-slate-600 dark:text-slate-400">Изменение цены</div>
                  {getPriceChange(selectedSeries) !== null ? (
                    <div className={`font-bold ${getPriceChange(selectedSeries) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {getPriceChange(selectedSeries) >= 0 ? '+' : ''}{getPriceChange(selectedSeries).toFixed(4)}%
                    </div>
                  ) : <div className="text-slate-500">—</div>}
                </div>
              </div>
              
              {/* Причина ошибки */}
              {(selectedSeries.status === 'failed' || selectedSeries.status === 'aborted') && selectedSeries.abort_reason && (
                <div className="bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded p-3">
                  <div className="text-xs text-red-700 dark:text-red-400 font-medium">Причина</div>
                  <div className="text-red-800 dark:text-red-300">{selectedSeries.abort_reason}</div>
                </div>
              )}
              
              {/* Анализ неудач */}
              {selectedSeries.failures > 0 && (
                <div className="bg-orange-100 dark:bg-orange-900/30 border border-orange-300 dark:border-orange-700 rounded p-3">
                  <div className="text-xs text-orange-700 dark:text-orange-400 font-medium">Неудачные операции</div>
                  <div className="text-orange-800 dark:text-orange-300">
                    {selectedSeries.failures} из {selectedSeries.successes + selectedSeries.failures} операций завершились с ошибкой
                    {selectedSeries.failures > selectedSeries.successes && (
                      <span className="block text-sm mt-1">
                        Возможные причины: недостаточно баланса, проблемы с сетью, rate limit
                      </span>
                    )}
                  </div>
                </div>
              )}
              
              {/* Время */}
              <div className="flex gap-4 text-sm text-slate-600 dark:text-slate-400">
                <span>Начало: {selectedSeries.started_at ? new Date(selectedSeries.started_at).toLocaleString('ru-RU') : '—'}</span>
                <span>Конец: {selectedSeries.completed_at ? new Date(selectedSeries.completed_at).toLocaleString('ru-RU') : '—'}</span>
              </div>
              
              {/* Цены */}
              {selectedSeries.start_price_usd && selectedSeries.end_price_usd && (
                <div className="text-sm">
                  <span className="text-slate-600 dark:text-slate-400">Цена: </span>
                  <span className="text-slate-900 dark:text-white">${selectedSeries.start_price_usd.toFixed(8)}</span>
                  <span className="text-slate-500 mx-2">→</span>
                  <span className={selectedSeries.end_price_usd >= selectedSeries.start_price_usd ? 'text-emerald-600' : 'text-red-600'}>
                    ${selectedSeries.end_price_usd.toFixed(8)}
                  </span>
                </div>
              )}
              
              {/* Таблица операций */}
              <div>
                <h4 className="text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Операции серии</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-slate-600 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                        <th className="py-2 px-2">#</th>
                        <th className="py-2 px-2">Время</th>
                        <th className="py-2 px-2">Операция</th>
                        <th className="py-2 px-2">Фаза</th>
                        <th className="py-2 px-2">Объём</th>
                        <th className="py-2 px-2">Результат</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedSeries.executed_steps || selectedSeries.planned_steps || []).map((step, idx) => (
                        <tr key={idx} className="border-b border-slate-100 dark:border-slate-800">
                          <td className="py-2 px-2 text-slate-500">{idx + 1}</td>
                          <td className="py-2 px-2 text-slate-700 dark:text-slate-300">
                            {step.executed_at ? new Date(step.executed_at).toLocaleTimeString('ru-RU') : '—'}
                          </td>
                          <td className="py-2 px-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              step.direction === 'buy' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'
                            }`}>
                              {step.direction === 'buy' ? 'ПОКУПКА' : 'ПРОДАЖА'}
                            </span>
                            <span className="ml-2 text-slate-700 dark:text-slate-300">{step.name}</span>
                          </td>
                          <td className="py-2 px-2 text-slate-500 text-xs">{step.phase}</td>
                          <td className="py-2 px-2 text-slate-700 dark:text-slate-300 font-medium">
                            ${(step.volume_usd || 0).toFixed(2)}
                          </td>
                          <td className="py-2 px-2">
                            {step.executed_at ? (
                              <span className={step.successes > 0 ? 'text-emerald-600' : 'text-red-600'}>
                                {step.successes || 0}✓ {step.failures || 0}✗
                              </span>
                            ) : (
                              <span className="text-slate-400">не выполнено</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Пагинация */}
      {total > limit && (
        <div className="flex justify-center gap-2 mt-4">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
          >
            ←
          </Button>
          <span className="text-sm text-slate-400 py-1">
            {page + 1} / {Math.ceil(total / limit)}
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setPage(p => p + 1)}
            disabled={(page + 1) * limit >= total}
          >
            →
          </Button>
        </div>
      )}
    </div>
  );
};

// Компонент анализа рынка
const MarketAnalysisCard = () => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadAnalysis = async () => {
      try {
        const baseUrl = `${API_URL}/api`;
        const res = await axios.get(`${baseUrl}/market-maker/market-analysis`);
        if (res.data.success) {
          setAnalysis(res.data.analysis);
        }
      } catch (e) {
        console.error('Error loading market analysis:', e);
      } finally {
        setLoading(false);
      }
    };
    
    loadAnalysis();
    const interval = setInterval(loadAnalysis, 30000); // Обновление каждые 30 сек
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return <div className="text-slate-500 text-sm">Загрузка анализа...</div>;
  }

  if (!analysis) {
    return <div className="text-slate-500 text-sm">Нет данных</div>;
  }

  const getTrendIcon = (trend) => {
    if (trend === 'bullish') return '📈';
    if (trend === 'bearish') return '📉';
    return '➡️';
  };

  const getTrendColor = (trend) => {
    if (trend === 'bullish') return 'text-emerald-500';
    if (trend === 'bearish') return 'text-red-500';
    return 'text-blue-500';
  };

  return (
    <div className="space-y-4">
      {/* Общий тренд */}
      <div className="flex items-center justify-between p-4 bg-gray-800 border border-gray-700 rounded-lg">
        <div>
          <div className="text-xs text-gray-400 mb-1">Общий тренд</div>
          <div className={`text-xl font-bold ${getTrendColor(analysis.overall_trend)} capitalize`}>
            {getTrendIcon(analysis.overall_trend)} {analysis.overall_trend}
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs text-gray-400 mb-1">Текущая цена</div>
          <div className="text-lg font-mono text-white font-bold">${(analysis.current_price || 0).toFixed(8)}</div>
        </div>
      </div>

      {/* Таймфреймы */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Object.entries(analysis.timeframes || {}).map(([tf, data]) => (
          <div key={tf} className="bg-gray-800 border border-gray-700 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-300 font-medium">{tf}</span>
              <span className={`text-sm ${getTrendColor(data.trend)}`}>
                {getTrendIcon(data.trend)}
              </span>
            </div>
            <div className={`text-base font-bold ${getTrendColor(data.trend)} capitalize mb-1`}>
              {data.trend || 'Unknown'}
            </div>
            <div className="text-sm text-gray-400">
              {data.confidence?.toFixed(0) || 0}% conf
            </div>
            <div className="mt-2 text-sm font-medium">
              <span className="text-green-400">${(data.buy_volume || 0).toFixed(0)}</span>
              {' / '}
              <span className="text-red-400">${(data.sell_volume || 0).toFixed(0)}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Рекомендация */}
      {analysis.recommendation && (
        <div className="p-4 bg-blue-900/30 border border-blue-600 rounded-lg">
          <div className="text-sm text-blue-300 font-medium mb-1">Рекомендация</div>
          <div className="text-base text-white">{analysis.recommendation.reason}</div>
        </div>
      )}
    </div>
  );
};

// Компонент Smart Series History
const SmartSeriesHistory = () => {
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ direction: '', status: '' });
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 10;

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const baseUrl = `${API_URL}/api`;
        const params = { limit, offset: page * limit };
        if (filter.direction) params.direction = filter.direction;
        if (filter.status) params.status = filter.status;
        
        const [historyRes, statsRes] = await Promise.all([
          axios.get(`${baseUrl}/market-maker/smart-series-history`, { params }),
          axios.get(`${baseUrl}/market-maker/smart-statistics`)
        ]);
        
        if (historyRes.data.success) {
          setHistory(historyRes.data.history || []);
          setTotal(historyRes.data.total || 0);
        }
        if (statsRes.data.success) {
          setStats(statsRes.data.statistics);
        }
      } catch (e) {
        console.error('Error loading smart series data:', e);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
    const interval = setInterval(loadData, 15000);
    return () => clearInterval(interval);
  }, [page, filter]);

  return (
    <div className="space-y-4">
      {/* Статистика по стратегиям */}
      {stats?.by_strategy && stats.by_strategy.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {stats.by_strategy.slice(0, 4).map((s) => (
            <div key={s.strategy_id} className="bg-gray-800 border border-gray-700 rounded-lg p-3">
              <div className="text-sm text-gray-300 truncate mb-1">{s.strategy_name || s.strategy_id}</div>
              <div className="text-2xl font-bold text-white">{s.total_series}</div>
              <div className="text-sm mt-1">
                <span className="text-green-400 font-medium">{s.completed}✓</span>
                {' / '}
                <span className="text-red-400 font-medium">{(s.aborted || 0) + (s.failed || 0)}✗</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Фильтры */}
      <div className="flex gap-2">
        <select
          className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-sm text-white"
          value={filter.direction}
          onChange={(e) => { setFilter({...filter, direction: e.target.value}); setPage(0); }}
        >
          <option value="">Все направления</option>
          <option value="bullish">Bullish</option>
          <option value="bearish">Bearish</option>
          <option value="neutral">Neutral</option>
        </select>
        <select
          className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-sm text-white"
          value={filter.status}
          onChange={(e) => { setFilter({...filter, status: e.target.value}); setPage(0); }}
        >
          <option value="">Все статусы</option>
          <option value="completed">Completed</option>
          <option value="aborted">Aborted</option>
          <option value="failed">Failed</option>
        </select>
      </div>

      {/* Таблица */}
      {loading && history.length === 0 ? (
        <div className="text-slate-500 text-sm">Загрузка...</div>
      ) : history.length === 0 ? (
        <div className="text-slate-500 text-sm text-center py-4">Нет данных</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-400 border-b border-slate-700">
                <th className="py-2 px-2">Время</th>
                <th className="py-2 px-2">Стратегия</th>
                <th className="py-2 px-2">Направление</th>
                <th className="py-2 px-2">Позиция</th>
                <th className="py-2 px-2">Цена</th>
                <th className="py-2 px-2">Статус</th>
              </tr>
            </thead>
            <tbody>
              {history.map((series) => (
                <tr key={series.series_id} className="border-b border-slate-800 hover:bg-slate-800/50">
                  <td className="py-2 px-2 text-slate-300">
                    {new Date(series.started_at).toLocaleString('ru', { 
                      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' 
                    })}
                  </td>
                  <td className="py-2 px-2">
                    <div className="text-white">{series.strategy_name}</div>
                    <div className="text-xs text-slate-500">{series.selection_reason?.slice(0, 40)}...</div>
                  </td>
                  <td className="py-2 px-2">
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      series.direction === 'bullish' ? 'bg-emerald-500/20 text-emerald-400' :
                      series.direction === 'bearish' ? 'bg-red-500/20 text-red-400' :
                      'bg-blue-500/20 text-blue-400'
                    }`}>
                      {series.direction}
                    </span>
                  </td>
                  <td className="py-2 px-2">
                    <span className={series.position_change_usd >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                      {series.position_change_usd >= 0 ? '+' : ''}{(series.position_change_usd || 0).toFixed(2)}
                    </span>
                  </td>
                  <td className="py-2 px-2">
                    <span className={series.price_change_percent >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                      {series.price_change_percent >= 0 ? '+' : ''}{(series.price_change_percent || 0).toFixed(3)}%
                    </span>
                  </td>
                  <td className="py-2 px-2">
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      series.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
                      series.status === 'aborted' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-red-500/20 text-red-400'
                    }`}>
                      {series.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Пагинация */}
      {total > limit && (
        <div className="flex justify-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>←</Button>
          <span className="text-sm text-slate-400 py-1">{page + 1} / {Math.ceil(total / limit)}</span>
          <Button size="sm" variant="outline" onClick={() => setPage(p => p + 1)} disabled={(page + 1) * limit >= total}>→</Button>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Control Center: единая строка модуля.
// Модуль АКТИВЕН ⟺ ⚡Питание ВКЛ (powerOn) И тумблер модуля ВКЛ (enabled).
//   • enabled=false            → серый  «Выключен»
//   • enabled=true, power=off  → жёлтый «Ждёт питания»
//   • enabled=true, power=on   → зелёный «Активен» (+ live sub-status)
// ============================================================================
const ModuleRow = ({ icon: Icon, name, description, enabled, powerOn, subStatus, onToggle, onOpen, busy }) => {
  const state = !enabled ? 'off' : (powerOn ? 'active' : 'armed');
  const dotClass = {
    active: 'bg-green-500 animate-pulse',
    armed: 'bg-amber-500',
    off: 'bg-slate-400'
  }[state];
  const stateLabel = {
    active: 'Активен',
    armed: 'Ждёт питания',
    off: 'Выключен'
  }[state];
  const stateText = {
    active: 'text-green-600',
    armed: 'text-amber-600',
    off: 'text-slate-400'
  }[state];

  return (
    <div className="flex items-center gap-3 rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2.5">
      <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${dotClass}`} />
      <Icon className="h-5 w-5 text-slate-500 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-semibold truncate">{name}</span>
          <span className={`text-xs font-medium ${stateText}`}>{stateLabel}</span>
          {state === 'active' && subStatus && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0">{subStatus}</Badge>
          )}
        </div>
        <p className="text-xs text-slate-500 truncate">{description}</p>
      </div>
      <button
        onClick={onOpen}
        className="text-xs text-blue-600 hover:underline shrink-0 hidden sm:inline"
      >
        Открыть →
      </button>
      <Switch checked={!!enabled} disabled={busy} onCheckedChange={onToggle} />
    </div>
  );
};

const MarketMaker = () => {
  // Token configuration from context
  const { tokenAddress: globalTokenAddress, tokenSymbol } = useTokenConfig();
  
  // Config state
  const [config, setConfig] = useState({
    enabled: false,
    min_enemy_volume_usd: 10.0,
    reaction_delay_seconds: 5.0,
    enemy_buy_sell_min_percent: 50.0,
    enemy_buy_sell_max_percent: 100.0,
    enemy_sell_buy_min_percent: 50.0,
    enemy_sell_buy_max_percent: 100.0,
    split_threshold_usd: 50.0,
    max_per_operation_usd: 1000.0,
    // Price Filter
    enable_price_filter: false,
    min_sell_price_increase_percent: 2.0,
    min_buy_price_decrease_percent: 2.0,
    // Grid Trading
    enable_grid_trading: false,
    grid_trading_mode: 'BOTH', // BOTH, BUY_ONLY, SELL_ONLY
    grid_sell_interval_percent: 5.0,
    grid_sell_amount_percent: 10.0,
    grid_buy_interval_percent: 5.0,
    grid_buy_amount_usd: 100.0,
    // SELL_ONLY mode settings
    grid_sell_on_enemy_buy: false,
    grid_sell_percent_of_enemy: 50.0,
    grid_sell_price_change_percent: 5.0,
    // Min transaction size
    min_transaction_multiplier: 6,
    // Grid Trading wallet selection
    grid_trading_wallet_addresses: []
  });

  // Status state
  const [status, setStatus] = useState({
    running: false,
    enabled: false
  });
  // AUDIT 2026-07-02: состояние kill-switch было НЕВИДИМО на странице MM.
  const [emergency, setEmergency] = useState({ stopped: false, reason: '', updatedBy: '' });

  // Stats state
  const [stats, setStats] = useState({
    total_operations: 0,
    successful_operations: 0,
    failed_operations: 0,
    total_volume_usd: 0,
    total_pnl_usd: 0,
    buys: 0,
    sells: 0
  });

  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('strategy'); // strategy, priceHolding, logs, gridTrading
  const [logs, setLogs] = useState([]);
  const [randomConfig, setRandomConfig] = useState(null);
  const [randomStatus, setRandomStatus] = useState(null);
  const [randomLoading, setRandomLoading] = useState(false);
  const [randomSaving, setRandomSaving] = useState(false);
  
  // Balance Preparation Engine state
  const [capacityStatus, setCapacityStatus] = useState(null);
  const [prepStatus, setPrepStatus] = useState(null);
  
  // Strategies state (multiple parallel strategies)
  const [strategies, setStrategies] = useState([]);

  // Любую ошибку (включая 422 с массивом detail) превращаем в СТРОКУ.
  // Иначе toast.error получает массив объектов → React "Objects are not valid as a React child" → белый экран.
  const errText = (error, fallback) => {
    const d = error?.response?.data?.detail;
    if (Array.isArray(d)) return d.map(e => `${e?.loc?.slice(-1)?.[0] ?? ''}: ${e?.msg ?? ''}`.trim()).filter(Boolean).join('; ') || fallback;
    if (typeof d === 'string') return d;
    if (d && typeof d === 'object') return d.msg || fallback;
    return fallback;
  };
  const [editingStrategy, setEditingStrategy] = useState(null);
  const [showStrategyDialog, setShowStrategyDialog] = useState(false);
  const [walletGroups, setWalletGroups] = useState([]);
  
  // Grid Trading series state
  const [seriesStatus, setSeriesStatus] = useState({
    series_id: null,
    status: 'NONE',
    base_price_usd: null,
    last_sell_price: null,
    last_buy_price: null,
    total_buys: 0,
    total_sells: 0,
    profit_loss_usd: 0
  });
  const [seriesHistory, setSeriesHistory] = useState([]);
  const [availableWallets, setAvailableWallets] = useState([]);

  // Price Holding state
  const [priceHoldingConfig, setPriceHoldingConfig] = useState({
    enabled: false,
    min_price_usd: 0.00012,
    buy_volume_percent: 50.0,
    max_total_buy_usd: 500.0,
    stop_at_floor: true,
    max_price_usd: 0.00015,
    sell_levels: 4,
    sell_volume_percent: 25.0,
    max_total_sell_percent: 80.0,
    check_interval_seconds: 30.0,
    react_delay_seconds: 5.0,
    slippage_percent: 0.3  // MM audit 1.3: admin-configurable (0.05-1.0% cap backend)
  });

  const [priceHoldingStatus, setPriceHoldingStatus] = useState({
    current_price: 0,
    at_floor: false,
    at_ceiling: false
  });

  // Average Buy Price state (for wallet cost basis tracking)
  const [avgBuyPrices, setAvgBuyPrices] = useState({});
  const [avgBuyPricesLoading, setAvgBuyPricesLoading] = useState(false);
  const [currentTokenPrice, setCurrentTokenPrice] = useState(0);
  
  // PNL Analytics state
  const [pnlData, setPnlData] = useState(null);
  const [pnlLoading, setPnlLoading] = useState(false);

  useEffect(() => {
    // Load critical data first (config, status)
    loadConfig();
    loadStatus();
    loadStats();
    loadPnlAnalytics();  // Load PNL data for header stats
    
    // Load history with loading indicator on initial load (WITH real-time for immediate display)

    loadRandomConfig();
    loadRandomStatus();
    loadPriceHoldingConfig();  // Control Center: показать состояние модуля Price Holding
    loadCapacityStatus();  // Balance Preparation Engine
    
    // Load other data in parallel (non-blocking)
    Promise.all([
      loadSeriesStatus(),
      loadSeriesHistory(),
      loadWallets(),
      loadStrategies(),
      loadWalletGroups()
    ]).catch(err => console.error('Error loading initial data:', err));

    // Refresh status and stats every 0.5 seconds для обновлений в реальном времени (reduced from 1s)
    // История обновляется каждую 0.5 секунды для максимально быстрого отображения свапов
    const interval = setInterval(() => {
      loadStatus();
      loadStats();
      loadRandomStatus();  // Control Center: live sub-status модуля Random & Smart Pulse
      // Обновляем историю каждую 0.5 секунды для реального времени (без индикатора загрузки)

      loadSeriesStatus();
      if (activeTab === 'gridTrading') {
        loadSeriesHistory();
      }
      if (activeTab === 'strategy') {
        loadStrategies();
      }
    }, 1000);  // poll 1s
    
    // PNL updates less frequently (every 30 seconds)
    const pnlInterval = setInterval(() => {
      loadPnlAnalytics();
    }, 30000);

    return () => {
      clearInterval(interval);
      clearInterval(pnlInterval);
    };
  }, [activeTab]);

  useEffect(() => {
    const interval = setInterval(() => {
      loadRandomStatus();
      loadCapacityStatus();  // Balance Preparation Engine
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const loadConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/market-maker/config`);
      if (response.data.success) {
        const loadedConfig = response.data.config;
        // Merge with defaults to ensure all fields are present
        setConfig(prevConfig => ({
          ...prevConfig,
          ...loadedConfig,
          // Ensure new Grid Trading fields have defaults
          grid_trading_mode: loadedConfig.grid_trading_mode ?? 'BOTH',
          grid_sell_on_enemy_buy: loadedConfig.grid_sell_on_enemy_buy ?? false,
          grid_sell_percent_of_enemy: loadedConfig.grid_sell_percent_of_enemy ?? 50.0,
          grid_sell_price_change_percent: loadedConfig.grid_sell_price_change_percent ?? 5.0,
        }));
      }
    } catch (error) {
      console.error('Error loading config:', error);
    }
  };

  const loadStrategies = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/market-maker/strategies`);
      const data = response?.data ?? {};
      // Some deployments may omit the success flag; accept data when array is present
      const strategiesList = Array.isArray(data?.strategies) ? data.strategies : (Array.isArray(data) ? data : []);
      console.log('Loaded strategies:', strategiesList.length);
      setStrategies(strategiesList);
      return strategiesList;
    } catch (error) {
      console.error('Error loading strategies:', error);
      return [];
    }
  };

  const loadWalletGroups = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/wallets/groups`);
      console.log('Groups API response:', response.data);
      // API returns { count, groups } - no 'success' field
      if (response.data && response.data.groups) {
        const groups = response.data.groups;
        console.log('Loaded groups:', groups.length, groups.map(g => g.group_name || g));
        setWalletGroups(groups);
      } else if (response.data && Array.isArray(response.data)) {
        // If API returns array directly
        setWalletGroups(response.data);
      } else {
        console.warn('Unexpected groups API response format:', response.data);
      }
    } catch (error) {
      console.error('Error loading wallet groups:', error);
      // Fallback: try to get groups from wallets list
      try {
        const walletsResponse = await axios.get(`${API_URL}/api/wallets/list`);
        if (walletsResponse.data && walletsResponse.data.wallets) {
          const uniqueGroups = [...new Set(walletsResponse.data.wallets.map(w => w.group_name).filter(Boolean))];
          console.log('Fallback: loaded groups from wallets:', uniqueGroups);
          setWalletGroups(uniqueGroups);
        }
      } catch (fallbackError) {
        console.error('Fallback error loading groups:', fallbackError);
      }
    }
  };

  const loadStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/market-maker/status`);
      if (response.data.success) {
        setStatus({
          running: response.data.running,
          enabled: response.data.enabled,
          // AUDIT 2026-07-02: honest-поля из /status (см. get_trading_health)
          tradingHealthy: response.data.trading_healthy,
          healthReason: response.data.health_reason,
          lastSuccessAt: response.data.last_success_at,
        });
      }
    } catch (error) {
      console.error('Error loading status:', error);
    }
    // AUDIT 2026-07-02: kill-switch state — отдельный системный роут. Ошибка не роняет страницу.
    try {
      const em = await axios.get(`${API_URL}/api/system/emergency-status`);
      setEmergency({
        stopped: !!em.data?.stopped,
        reason: em.data?.reason || '',
        updatedBy: em.data?.updated_by || '',
      });
    } catch (e) {
      // без баннера — не критично
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/market-maker/stats`);
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadRandomConfig = async () => {
    setRandomLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/market-maker/random-config`);
      if (response.data.success) {
        setRandomConfig(response.data.config);
      } else {
        setRandomConfig(null);
      }
    } catch (error) {
      console.error('Error loading random config:', error);
      setRandomConfig(null);
    } finally {
      setRandomLoading(false);
    }
  };

  const saveRandomConfig = async () => {
    if (!randomConfig) return;
    setRandomSaving(true);
    try {
      await axios.post(`${API_URL}/api/market-maker/random-config`, randomConfig);
      toast.success('Random configuration saved');
      await loadRandomConfig();
      await loadRandomStatus();
    } catch (error) {
      console.error('Error saving random config:', error);
      toast.error('Failed to save random configuration');
    } finally {
      setRandomSaving(false);
    }
  };

  // ⚡ Smart Auto preset — один клик: выставить разумные дефолты автопилота.
  // Если выбрана группа кошельков — сразу сохраняет и запускает движок.
  // Если нет — применяет настройки в форму и просит выбрать кошельки.
  const applySmartAutoPreset = async () => {
    if (!randomConfig) return;
    const preset = {
      ...randomConfig,
      strategy_mode: 'smart_auto',
      position_tracking_enabled: true,
      max_net_position_usd: randomConfig.max_net_position_usd || 500,
      soft_rebalance_threshold: 0.3,
      hard_rebalance_threshold: 0.6,
      critical_rebalance_threshold: 0.8,
      max_consecutive_same_direction: 3,
      history_lookback_series: 5,
      series_balance_ratio: 0.85,
      interval_min_seconds: randomConfig.interval_min_seconds || 30,
      interval_max_seconds: randomConfig.interval_max_seconds || 90,
      volume_min_usd: randomConfig.volume_min_usd || 10,
      volume_max_usd: randomConfig.volume_max_usd || 100,
    };

    // Без группы кошельков серии будут падать "no funds" — не запускаем, просим выбрать.
    if (!preset.primary_wallet_group) {
      setRandomConfig(preset);
      toast.warning('Smart Auto настройки применены. Выбери «Основная группа кошельков» ниже и нажми Save Random Settings.');
      return;
    }

    preset.enabled = true;
    setRandomConfig(preset);
    setRandomSaving(true);
    try {
      await axios.post(`${API_URL}/api/market-maker/random-config`, preset);
      await loadStatus();
      await loadRandomStatus();
      toast.success('⚡ Smart Auto применён и запущен');
    } catch (e) {
      toast.error('Не удалось применить Smart Auto preset');
    } finally {
      setRandomSaving(false);
    }
  };

  const loadRandomStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/market-maker/random-status`);
      if (response.data.success) {
        setRandomStatus(response.data.status);
      }
    } catch (error) {
      console.error('Error loading random status:', error);
    }
  };

  // Balance Preparation Engine - загрузка capacity
  const loadCapacityStatus = async () => {
    try {
      const [capacityRes, prepRes] = await Promise.all([
        axios.get(`${API_URL}/api/market-maker/capacity`),
        axios.get(`${API_URL}/api/market-maker/prep-status`)
      ]);
      
      if (capacityRes.data.success) {
        setCapacityStatus(capacityRes.data.capacity);
      }
      if (prepRes.data.success) {
        setPrepStatus(prepRes.data.prep);
      }
    } catch (error) {
      console.error('Error loading capacity status:', error);
    }
  };

  const loadLogs = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/market-maker/logs`, {
        params: { limit: LOG_FETCH_LIMIT }
      });
      if (response.data.success) {
        const normalizedLogs = (response.data.logs || []).map((log, index) => {
          if (typeof log === 'string') {
            const match = log.match(/^\[(\w+)\]\s+(.*)$/);
            return {
              id: `legacy-${index}`,
              timestamp: null,
              level: match?.[1] || 'INFO',
              message: match?.[2] || log
            };
          }
          return {
            id: log.id ?? `${log.timestamp ?? 'log'}-${index}`,
            timestamp: log.timestamp || null,
            level: log.level || 'INFO',
            message: log.message || ''
          };
        });
        setLogs(normalizedLogs);
      }
    } catch (error) {
      console.error('Error loading logs:', error);
    }
  };

  // Grid Trading functions
  const loadSeriesStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/market-maker/series/status`);
      if (response.data.success) {
        setSeriesStatus(response.data.series);
      }
    } catch (error) {
      console.error('Error loading series status:', error);
    }
  };

  const loadSeriesHistory = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/market-maker/series/history?limit=50`);
      if (response.data.success) {
        setSeriesHistory(response.data.history);
      }
    } catch (error) {
      console.error('Error loading series history:', error);
    }
  };

  const startSeries = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/market-maker/series/start`);
      if (response.data.success) {
        toast.success('Trading series started');
        await loadSeriesStatus();
        await loadSeriesHistory();
      }
    } catch (error) {
      console.error('Error starting series:', error);
      toast.error('Failed to start series');
    }
  };

  const stopSeries = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/market-maker/series/stop`);
      if (response.data.success) {
        toast.success('Trading series stopped');
        await loadSeriesStatus();
      }
    } catch (error) {
      console.error('Error stopping series:', error);
      toast.error('Failed to stop series');
    }
  };

  const loadWallets = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/wallets/list`);
      if (response.data.wallets) {
        setAvailableWallets(response.data.wallets);
      }
    } catch (error) {
      console.error('Error loading wallets:', error);
    }
  };

  const loadAvgBuyPrices = async () => {
    try {
      setAvgBuyPricesLoading(true);
      const response = await axios.get(`${API_URL}/api/wallet-history/all-wallets/average-buy-prices`);
      if (response.data.success) {
        setAvgBuyPrices(response.data.data || {});
      }
    } catch (error) {
      console.error('Error loading average buy prices:', error);
    } finally {
      setAvgBuyPricesLoading(false);
    }
  };

  const loadCurrentTokenPrice = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/market-maker/current-token-price`);
      if (response.data.success && response.data.price_usd) {
        setCurrentTokenPrice(response.data.price_usd);
      }
    } catch (error) {
      console.error('Error loading token price:', error);
      // Fallback: try to get from series status
      if (seriesStatus?.base_price_usd) {
        setCurrentTokenPrice(seriesStatus.base_price_usd);
      }
    }
  };

  const [recalculating, setRecalculating] = useState(false);
  const [recalcTaskId, setRecalcTaskId] = useState(null);
  const [recalcStatus, setRecalcStatus] = useState(null);
  const [recalcInterval, setRecalcInterval] = useState(null);

  const recalculateAveragePrice = async () => {
    if (!confirm('Пересчитать среднюю цену на основе данных из блокчейна? Это может занять некоторое время.')) {
      return;
    }
    
    setRecalculating(true);
    setRecalcStatus(null);
    try {
      const response = await axios.post(`${API_URL}/api/wallet-history/recalculate-average-price`, {}, {
        params: {
          limit_per_wallet: 200
        }
      });
      
      if (response.data.success && response.data.task_id) {
        setRecalcTaskId(response.data.task_id);
        
        // Начать polling статуса
        const interval = setInterval(async () => {
          try {
            const statusResponse = await axios.get(`${API_URL}/api/wallet-history/recalculate-average-price/status/${response.data.task_id}`);
            if (statusResponse.data.success) {
              const status = statusResponse.data.status;
              setRecalcStatus(status);
              
              if (status.status === 'completed' || status.status === 'failed') {
                clearInterval(interval);
                setRecalcInterval(null);
                setRecalculating(false);
                
                if (status.status === 'completed') {
                  alert(`Пересчет завершен!\n\nОбработано кошельков: ${status.wallets_processed || 0}\nНайдено свапов: ${status.swaps_found || 0}\nДобавлено новых операций: ${status.swaps_added || 0}`);
                  // Обновить данные после пересчета
                  loadPnlAnalytics();
                  loadAvgBuyPrices();
                } else {
                  alert(`Ошибка пересчета: ${status.error || 'Неизвестная ошибка'}`);
                }
              }
            }
          } catch (error) {
            console.error('Error checking recalculation status:', error);
          }
        }, 2000); // Проверять каждые 2 секунды
        
        setRecalcInterval(interval);
      } else {
        alert(`Ошибка запуска пересчета: ${response.data.error || 'Неизвестная ошибка'}`);
        setRecalculating(false);
      }
    } catch (error) {
      console.error('Error starting recalculation:', error);
      alert(`Ошибка: ${error.response?.data?.detail || error.message}`);
      setRecalculating(false);
    }
  };

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (recalcInterval) {
        clearInterval(recalcInterval);
      }
    };
  }, [recalcInterval]);

  const loadPnlAnalytics = async () => {
    try {
      setPnlLoading(true);
      const response = await axios.get(`${API_URL}/api/wallet-history/all-wallets/pnl-analytics`);
      if (response.data.success) {
        setPnlData(response.data);
        setCurrentTokenPrice(response.data.current_token_price);
      }
    } catch (error) {
      console.error('Error loading PNL analytics:', error);
    } finally {
      setPnlLoading(false);
    }
  };

  const toggleWalletSelection = (address) => {
    const current = config.grid_trading_wallet_addresses || [];
    if (current.includes(address)) {
      setConfig({
        ...config,
        grid_trading_wallet_addresses: current.filter(a => a !== address)
      });
    } else {
      setConfig({
        ...config,
        grid_trading_wallet_addresses: [...current, address]
      });
    }
  };

  // мемо: список кошелькоов не перерисовывается на каждый 1с-поллинг (deps без polled-state)
  const walletSelectorEls = useMemo(() => availableWallets.map((wallet) => {
                      // Get token balance from jetton_balances
                      const tokenAddress = config.token_address || globalTokenAddress;
                      const buytReadable = jettonReadable(wallet.jetton_balances, tokenAddress, globalTokenAddress);
                      const tonReadable = (wallet.ton_balance || 0) / 1e9;
                      
                      // Format with thousand separators and no decimals
                      const formatNumber = (num) => {
                        return Math.floor(num).toLocaleString('en-US');
                      };
                      
                      return (
                        <div
                          key={wallet.address}
                          className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                            config.grid_trading_wallet_addresses?.includes(wallet.address)
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => toggleWalletSelection(wallet.address)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="font-mono text-xs break-all">
                                {wallet.address}
                              </div>
                              <div className="mt-1 space-y-0.5">
                                <div className="text-xs text-gray-600">
                                  TON: {formatNumber(tonReadable)}
                                </div>
                                <div className={`text-xs font-medium ${buytReadable > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                                  {tokenSymbol || 'TOKEN'}: {formatNumber(buytReadable)}
                                </div>
                              </div>
                            </div>
                            <input
                              type="checkbox"
                              checked={config.grid_trading_wallet_addresses?.includes(wallet.address) || false}
                              onChange={() => {}}
                              className="ml-2 h-4 w-4 flex-shrink-0"
                            />
                          </div>
                        </div>
                      );
                    }), [availableWallets, config, globalTokenAddress, tokenSymbol]);

  const selectAllWallets = () => {
    setConfig({
      ...config,
      grid_trading_wallet_addresses: availableWallets.map(w => w.address)
    });
  };

  const deselectAllWallets = () => {
    setConfig({
      ...config,
      grid_trading_wallet_addresses: []
    });
  };

  const selectWalletsWithBUYT = () => {
    const tokenAddress = config.token_address || globalTokenAddress;
    const walletsWithBUYT = availableWallets.filter(w => {
      const buytBalance = jettonReadable(w.jetton_balances, tokenAddress, globalTokenAddress);
      return buytBalance > 0;
    }).map(w => w.address);
    
    setConfig({
      ...config,
      grid_trading_wallet_addresses: walletsWithBUYT
    });
  };

  const selectWalletsWithTON = () => {
    const walletsWithTON = availableWallets.filter(w => (w.ton_balance || 0) > 0).map(w => w.address);
    setConfig({
      ...config,
      grid_trading_wallet_addresses: walletsWithTON
    });
  };

  const selectEmptyWallets = () => {
    const tokenAddress = config.token_address || globalTokenAddress;
    const emptyWallets = availableWallets.filter(w => {
      const buytBalance = jettonReadable(w.jetton_balances, tokenAddress, globalTokenAddress);
      const tonBalance = w.ton_balance || 0;
      return buytBalance === 0 && tonBalance === 0;
    }).map(w => w.address);
    
    setConfig({
      ...config,
      grid_trading_wallet_addresses: emptyWallets
    });
  };

  const selectWalletsByGroup = (groupName) => {
    console.log('Selecting wallets for group:', groupName);
    
    const groupWallets = availableWallets.filter(w => {
      const wGroupName = w.group_name || 'No Group';
      return wGroupName === groupName;
    }).map(w => w.address);
    
    console.log('Found wallets:', groupWallets.length);
    
    setConfig({
      ...config,
      grid_trading_wallet_addresses: groupWallets
    });
  };

  const getWalletGroups = () => {
    const groups = {};
    availableWallets.forEach(wallet => {
      const groupName = wallet.group_name || 'No Group';
      if (!groups[groupName]) {
        groups[groupName] = {
          name: groupName,
          count: 0,
          totalTON: 0,
          totalBUYT: 0
        };
      }
      groups[groupName].count++;
      groups[groupName].totalTON += (wallet.ton_balance || 0) / 1e9;

      const tokenAddress = config.token_address || globalTokenAddress;
      groups[groupName].totalBUYT += jettonReadable(wallet.jetton_balances, tokenAddress, globalTokenAddress);
    });
    return Object.values(groups);
  };

  const saveConfig = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/market-maker/config`, config);
      toast.success('Configuration saved successfully');
      await loadConfig();
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setLoading(false);
    }
  };

  const startStrategy = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/market-maker/start`);
      toast.success('Market maker strategy started!');
      await loadStatus();
    } catch (error) {
      toast.error(errText(error, 'Failed to start strategy'));
    } finally {
      setLoading(false);
    }
  };

  const stopStrategy = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/market-maker/stop`);
      toast.success('Market maker strategy stopped');
      await loadStatus();
    } catch (error) {
      toast.error('Failed to stop strategy');
    } finally {
      setLoading(false);
    }
  };

  // ===== Control Center: быстрые тумблеры модулей =====
  // Каждый тумблер сохраняет флаг `enabled` соответствующего модуля напрямую
  // (без гонок состояния), затем обновляет статус. Random & Smart Pulse после
  // сохранения сам поднимает движок на бэкенде; остальные модули применяются
  // при следующем запуске ⚡Питания.
  const toggleCounterTrading = async (enabled) => {
    setLoading(true);
    try {
      const next = { ...config, enabled };
      await axios.post(`${API_URL}/api/market-maker/config`, next);
      setConfig(next);
      toast.success(enabled ? 'Counter-Trading включён' : 'Counter-Trading выключен');
      await loadStatus();
    } catch (e) {
      toast.error('Не удалось переключить Counter-Trading');
    } finally {
      setLoading(false);
    }
  };

  const toggleRandomModule = async (enabled) => {
    if (!randomConfig) return;
    setRandomSaving(true);
    try {
      const next = { ...randomConfig, enabled };
      await axios.post(`${API_URL}/api/market-maker/random-config`, next);
      setRandomConfig(next);
      toast.success(enabled ? 'Random & Smart Pulse включён — движок запускается' : 'Random & Smart Pulse выключен');
      await loadStatus();
      await loadRandomStatus();
    } catch (e) {
      toast.error('Не удалось переключить Random & Smart Pulse');
    } finally {
      setRandomSaving(false);
    }
  };

  const togglePriceHolding = async (enabled) => {
    setLoading(true);
    try {
      const next = { ...priceHoldingConfig, enabled };
      await axios.post(`${API_URL}/api/market-maker/price-holding/config`, next);
      setPriceHoldingConfig(next);
      toast.success(enabled ? 'Price Holding включён' : 'Price Holding выключен');
      await loadStatus();
    } catch (e) {
      toast.error('Не удалось переключить Price Holding');
    } finally {
      setLoading(false);
    }
  };

  const toggleGridModule = async (enabled) => {
    setLoading(true);
    try {
      const next = { ...config, enable_grid_trading: enabled };
      await axios.post(`${API_URL}/api/market-maker/config`, next);
      setConfig(next);
      toast.success(enabled ? 'Grid Trading включён' : 'Grid Trading выключен');
      await loadStatus();
    } catch (e) {
      toast.error('Не удалось переключить Grid Trading');
    } finally {
      setLoading(false);
    }
  };

  // Strategy management functions
  const createStrategy = async (strategyData) => {
    try {
      const response = await axios.post(`${API_URL}/api/market-maker/strategies`, strategyData);
      if (response.data.success) {
        toast.success('Strategy created successfully');
        // Reload strategies and wait for state update
        const updatedStrategies = await loadStrategies();
        console.log('Strategies after creation:', updatedStrategies.length);
        // Small delay to ensure React has updated the state before closing dialog
        await new Promise(resolve => setTimeout(resolve, 200));
        setShowStrategyDialog(false);
        setEditingStrategy(null);
      }
    } catch (error) {
      toast.error(errText(error, 'Failed to create strategy'));
    }
  };

  const updateStrategy = async (strategyId, strategyData) => {
    try {
      const response = await axios.put(`${API_URL}/api/market-maker/strategies/${strategyId}`, strategyData);
      if (response.data.success) {
        toast.success('Strategy updated successfully');
        await loadStrategies();
        // Small delay to ensure React has updated the state before closing dialog
        await new Promise(resolve => setTimeout(resolve, 100));
        setShowStrategyDialog(false);
        setEditingStrategy(null);
      }
    } catch (error) {
      toast.error(errText(error, 'Failed to update strategy'));
    }
  };

  const deleteStrategy = async (strategyId) => {
    if (!confirm('Are you sure you want to delete this strategy?')) {
      return;
    }
    try {
      const response = await axios.delete(`${API_URL}/api/market-maker/strategies/${strategyId}`);
      if (response.data.success) {
        toast.success('Strategy deleted successfully');
        await loadStrategies();
      }
    } catch (error) {
      toast.error(errText(error, 'Failed to delete strategy'));
    }
  };

  const toggleStrategyEnabled = async (strategyId, enabled) => {
    try {
      const strategy = strategies.find(s => s.id === strategyId);
      if (!strategy) return;
      
      const updatedStrategy = { ...strategy, enabled: !enabled };
      const response = await axios.put(`${API_URL}/api/market-maker/strategies/${strategyId}`, updatedStrategy);
      if (response.data.success) {
        toast.success(`Strategy ${enabled ? 'disabled' : 'enabled'}`);
        await loadStrategies();
      }
    } catch (error) {
      toast.error(errText(error, 'Failed to toggle strategy'));
    }
  };

  const handleEditStrategy = async (strategy) => {
    setEditingStrategy(strategy);
    // Ensure groups are loaded before opening dialog
    await loadWalletGroups();
    setShowStrategyDialog(true);
  };

  const handleNewStrategy = async () => {
    setEditingStrategy(null);
    // Ensure groups are loaded before opening dialog
    await loadWalletGroups();
    setShowStrategyDialog(true);
  };

  // Price Holding functions
  const loadPriceHoldingConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/market-maker/price-holding/config`);
      if (response.data.success) {
        setPriceHoldingConfig(response.data.config);
        if (response.data.current_price) {
          setPriceHoldingStatus(prev => ({
            ...prev,
            current_price: response.data.current_price
          }));
        }
      }
    } catch (error) {
      console.error('Error loading price holding config:', error);
    }
  };

  const loadPriceHoldingStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/market-maker/price-holding/status`);
      if (response.data.success) {
        setPriceHoldingStatus({
          current_price: response.data.current_price,
          at_floor: response.data.at_floor,
          at_ceiling: response.data.at_ceiling
        });
      }
    } catch (error) {
      console.error('Error loading price holding status:', error);
    }
  };

  const savePriceHoldingConfig = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/market-maker/price-holding/config`, priceHoldingConfig);
      toast.success('Price holding configuration saved!');
      await loadPriceHoldingConfig();
    } catch (error) {
      toast.error('Failed to save price holding configuration');
    } finally {
      setLoading(false);
    }
  };

  // Reload strategies when dialog closes
  useEffect(() => {
    if (!showStrategyDialog && activeTab === 'strategy') {
      // Dialog was closed, reload strategies to ensure list is up to date
      loadStrategies();
    }
  }, [showStrategyDialog, activeTab]);

  // Load price holding data when tab is active
  useEffect(() => {
    if (activeTab === 'priceHolding') {
      loadPriceHoldingConfig();
      loadPriceHoldingStatus();
      
      const interval = setInterval(() => {
        loadPriceHoldingStatus();
      }, 5000);
      
      return () => clearInterval(interval);
    } else if (activeTab === 'logs') {
      loadLogs();
      
      const interval = setInterval(() => {
        loadLogs();
      }, 3000); // Refresh logs every 3 seconds
      
      return () => clearInterval(interval);
    }
  }, [activeTab]);

  const categorizedLogs = useMemo(() => {
    const categories = {
      all: [],
      actions: [],
      errors: [],
      info: []
    };

    logs.forEach((entry) => {
      const normalizedEntry = {
        ...entry,
        level: (entry.level || 'INFO').toUpperCase()
      };

      categories.all.push(normalizedEntry);

      if (normalizedEntry.level === 'SUCCESS') {
        categories.actions.push(normalizedEntry);
      } else if (normalizedEntry.level === 'ERROR') {
        categories.errors.push(normalizedEntry);
      } else {
        categories.info.push(normalizedEntry);
      }
    });

    return categories;
  }, [logs]);

  const formatLogTimestamp = (timestamp) => {
    if (!timestamp) {
      return '—';
    }
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) {
      return timestamp;
    }
    return date.toLocaleString();
  };

  const getLevelBadgeClass = (level) => {
    switch (level) {
      case 'SUCCESS':
        return 'border border-emerald-500/40 bg-emerald-500/10 text-emerald-300';
      case 'ERROR':
        return 'border border-red-500/40 bg-red-500/10 text-red-300';
      case 'WARNING':
        return 'border border-yellow-500/40 bg-yellow-500/10 text-yellow-300';
      default:
        return 'border border-blue-500/40 bg-blue-500/10 text-blue-300';
    }
  };

  const renderAllLogsTable = (entries) => {
    if (!entries || entries.length === 0) {
      return (
        <div className="rounded-lg border border-gray-800 bg-gray-950/80 p-10 text-center text-sm text-gray-500">
          Нет логов. Запустите стратегию, чтобы увидеть новые записи.
        </div>
      );
    }

    return (
      <div className="overflow-hidden rounded-lg border border-gray-800 bg-gray-950/80">
        <div className="grid grid-cols-[160px_120px_1fr] border-b border-gray-800 bg-gray-900/60 px-4 py-2 text-[11px] uppercase tracking-wide text-gray-400">
          <span>Время</span>
          <span>Категория</span>
          <span>Сообщение</span>
        </div>
        <div className="max-h-[520px] overflow-y-auto">
          {entries.map((entry, idx) => (
            <div
              key={entry.id || idx}
              className="grid grid-cols-[160px_120px_1fr] border-b border-gray-900/40 px-4 py-3 text-sm text-gray-100 hover:bg-gray-900/40"
            >
              <div className="font-mono text-xs text-gray-300">
                {formatLogTimestamp(entry.timestamp)}
              </div>
              <div>
                <span className={`inline-flex items-center rounded px-2 py-0.5 text-[10px] font-semibold ${getLevelBadgeClass(entry.level)}`}>
                  {entry.level}
                </span>
              </div>
              <div className="whitespace-pre-wrap text-xs leading-relaxed md:text-sm">
                {entry.message}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderCategoryPanel = (title, description, entries, accentClass) => (
    <div className="rounded-lg border border-gray-800 bg-gray-950/80 p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-sm font-semibold text-gray-200">{title}</div>
          <div className="text-xs text-gray-500">{description}</div>
        </div>
        <span className={`rounded-full border border-gray-800 px-3 py-1 text-xs font-semibold ${accentClass}`}>
          {entries.length}
        </span>
      </div>
      {entries.length === 0 ? (
        <div className="mt-4 rounded-md border border-dashed border-gray-800 bg-gray-900/40 p-4 text-center text-xs text-gray-500">
          Нет записей в этой категории
        </div>
      ) : (
        <div className="mt-4 max-h-72 space-y-2 overflow-y-auto pr-1">
          {entries.map((entry) => (
            <div
              key={entry.id}
              className="rounded-md border border-gray-800 bg-gray-950/90 p-3 text-gray-100"
            >
              <div className="flex items-center justify-between text-[11px] uppercase tracking-wide text-gray-400">
                <span className="font-mono normal-case text-xs text-gray-300">
                  {formatLogTimestamp(entry.timestamp)}
                </span>
                <span className={`rounded px-2 py-0.5 text-[10px] font-semibold ${getLevelBadgeClass(entry.level)}`}>
                  {entry.level}
                </span>
              </div>
              <div className="mt-2 whitespace-pre-wrap text-xs leading-relaxed md:text-sm">
                {entry.message}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Market Maker</h1>
          <p className="text-gray-500 mt-1">Единый пульт управления модулями маркетмейкинга</p>
        </div>
      </div>

      {/* AUDIT 2026-07-02: видимость аварийного стопа (был невидим на этой странице) */}
      {emergency.stopped && (
        <div className="rounded-lg border-2 border-red-500 bg-red-50 dark:bg-red-950 p-4 flex items-start gap-3">
          <ShieldAlert className="h-6 w-6 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-bold text-red-700 dark:text-red-400">Аварийный стоп АКТИВЕН — торговля остановлена</div>
            <div className="text-sm text-red-600 dark:text-red-300 mt-0.5">
              {emergency.reason ? `Причина: ${emergency.reason}. ` : ''}
              {emergency.updatedBy ? `Инициатор: ${emergency.updatedBy}. ` : ''}
              Снять может только admin (POST /api/system/resume).
            </div>
          </div>
        </div>
      )}
      {/* AUDIT 2026-07-02: honest-health — бот «молча простаивал» при running:true */}
      {status.tradingHealthy === false && !emergency.stopped && (
        <div className="rounded-lg border-2 border-amber-500 bg-amber-50 dark:bg-amber-950 p-4 flex items-start gap-3">
          <ShieldAlert className="h-6 w-6 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-bold text-amber-700 dark:text-amber-400">Торговля не исполняется</div>
            <div className="text-sm text-amber-600 dark:text-amber-300 mt-0.5">
              Есть попытки, но 0 успешных сделок за 24ч.
              {status.lastSuccessAt ? ` Последняя успешная сделка: ${new Date(status.lastSuccessAt).toLocaleString()}.` : ''}
              {' '}Проверьте газ/токены на кошельках группы стратегии.
            </div>
          </div>
        </div>
      )}

      <DumpBuyback />

      {/* ============ CONTROL CENTER — единый пульт ============ */}
      <Card className="border-2 border-slate-300 dark:border-slate-600">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className={`flex h-11 w-11 items-center justify-center rounded-full ${status.running ? 'bg-green-100 dark:bg-green-900' : 'bg-slate-100 dark:bg-slate-800'}`}>
                <Power className={`h-6 w-6 ${status.running ? 'text-green-600' : 'text-slate-400'}`} />
              </div>
              <div>
                <CardTitle className="flex items-center gap-2">
                  Питание движка
                  <Badge
                    variant={status.running ? 'default' : 'secondary'}
                    className={status.running ? 'bg-green-600' : ''}
                  >
                    {status.running ? (
                      <><Activity className="h-3.5 w-3.5 mr-1 animate-pulse" /> RUNNING</>
                    ) : 'STOPPED'}
                  </Badge>
                </CardTitle>
                <CardDescription>
                  Общий рубильник. Модуль работает только когда питание ВКЛ и его тумблер ВКЛ.
                </CardDescription>
              </div>
            </div>
            <div>
              {!status.running ? (
                <Button
                  onClick={startStrategy}
                  disabled={loading || !(config.enabled || randomConfig?.enabled || priceHoldingConfig?.enabled || config.enable_grid_trading)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Play className="h-4 w-4 mr-2" /> Запустить питание
                </Button>
              ) : (
                <Button onClick={stopStrategy} disabled={loading} variant="destructive">
                  <Pause className="h-4 w-4 mr-2" /> Остановить питание
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          <ModuleRow
            icon={Target}
            name="Counter-Trading"
            description="Контр-сделки в ответ на свапы чужих кошельков"
            enabled={!!config.enabled}
            powerOn={status.running}
            onToggle={toggleCounterTrading}
            onOpen={() => setActiveTab('strategy')}
            busy={loading}
          />
          <ModuleRow
            icon={Zap}
            name="Random & Smart Pulse"
            description="Сам генерит объём. Режим Smart = адаптивный выбор стратегии внутри этого модуля"
            enabled={!!randomConfig?.enabled}
            powerOn={status.running}
            subStatus={randomStatus?.status && randomStatus.status !== 'disabled' ? randomStatus.status : null}
            onToggle={toggleRandomModule}
            onOpen={() => setActiveTab('random')}
            busy={randomSaving || !randomConfig}
          />
          <ModuleRow
            icon={Anchor}
            name="Price Holding"
            description="Удержание цены в заданном коридоре (откуп у пола / продажа у потолка)"
            enabled={!!priceHoldingConfig?.enabled}
            powerOn={status.running}
            onToggle={togglePriceHolding}
            onOpen={() => setActiveTab('priceHolding')}
            busy={loading}
          />
          <ModuleRow
            icon={Grid3x3}
            name="Grid Trading"
            description="Сетка ордеров по уровням цены"
            enabled={!!config.enable_grid_trading}
            powerOn={status.running}
            onToggle={toggleGridModule}
            onOpen={() => setActiveTab('gridTrading')}
            busy={loading}
          />
          <p className="text-xs text-slate-500 pt-1">
            ⚡ Тумблеры задают, какие модули включены. Нажми «Запустить питание», чтобы применить.
            <span className="text-slate-400"> Random &amp; Smart Pulse применяется сразу при включении.</span>
          </p>
        </CardContent>
      </Card>

      {/* MM audit 3.2: DRY_RUN toggle */}
      <DryRunCard />

      {/* MM audit 2.4: Daily PnL breakdown (live) */}
      <DailyPnLCard />

      {/* MM audit 3.1: TWAP buyback executor */}
      <BuybackCard />

      {/* MM audit 3.4: Bounce detector */}
      <BounceCard />

      {/* MM audit 3.5: DEX health monitor */}
      <DexHealthCard />

      {/* MM audit 3.6: Toxic-flow detector */}
      <ToxicFlowCard />

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Total Operations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total_operations}</div>
            <p className="text-xs text-gray-500 mt-1">
              ✓ {stats.successful_operations} success, ✗ {stats.failed_operations} failed
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Total Volume</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${stats.total_volume_usd.toFixed(2)}</div>
            <p className="text-xs text-gray-500 mt-1">
              ↑ {stats.buys} buys, ↓ {stats.sells} sells
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Total PNL</CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${(pnlData?.totals?.total_pnl || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {(pnlData?.totals?.total_pnl || 0) >= 0 ? '+' : '-'}${Math.abs(pnlData?.totals?.total_pnl || 0).toFixed(2)}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              ROI: {(pnlData?.totals?.roi_percent || 0) >= 0 ? '+' : ''}{(pnlData?.totals?.roi_percent || 0).toFixed(1)}%
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Success Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats.total_operations > 0 
                ? ((stats.successful_operations / stats.total_operations) * 100).toFixed(1)
                : 0}%
            </div>
            <p className="text-xs text-gray-500 mt-1">Overall success rate</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs Navigation */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setActiveTab('strategy')}
          className={`px-6 py-3 font-medium transition-colors ${
            activeTab === 'strategy'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Counter-Trading Strategy
        </button>
        <button
          onClick={() => setActiveTab('priceHolding')}
          className={`px-6 py-3 font-medium transition-colors ${
            activeTab === 'priceHolding'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Price Holding
        </button>
        <button
          onClick={() => setActiveTab('random')}
          className={`px-6 py-3 font-medium transition-colors ${
            activeTab === 'random'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Random Pulses
        </button>
        <button
          onClick={() => setActiveTab('gridTrading')}
          className={`px-6 py-3 font-medium transition-colors ${
            activeTab === 'gridTrading'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Grid Trading
        </button>
        <button
          onClick={() => {
            setActiveTab('avgPrices');
            loadPnlAnalytics();
            loadAvgBuyPrices();
            loadWallets();
          }}
          className={`px-6 py-3 font-medium transition-colors ${
            activeTab === 'avgPrices'
              ? 'border-b-2 border-amber-600 text-amber-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          PNL Аналитика
        </button>
        <button
          onClick={() => setActiveTab('logs')}
          className={`px-6 py-3 font-medium transition-colors ${
            activeTab === 'logs'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          System Logs
        </button>
      </div>

      {/* Strategy Tab */}
      {activeTab === 'strategy' && (
        <>
          {/* Strategies List */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="h-5 w-5" />
                    Counter-Trading Strategies
                  </CardTitle>
                  <CardDescription>
                    Управление параллельными стратегиями для разных групп кошельков
                  </CardDescription>
                </div>
                <Button onClick={handleNewStrategy} className="flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  Создать стратегию
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {strategies.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>Нет стратегий. Создайте первую стратегию для начала работы.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {strategies.map((strategy) => (
                    <Card key={strategy.id} className="border-l-4 border-l-blue-500">
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="font-semibold text-lg">{strategy.name}</h3>
                              <Badge variant={strategy.enabled ? "default" : "secondary"}>
                                {strategy.enabled ? "Включена" : "Выключена"}
                              </Badge>
                              {strategy.enable_avg_cost_protection && (
                                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300">
                                  <Target className="h-3 w-3 mr-1" />
                                  Защита цены +{strategy.min_profit_margin_percent}%
                                </Badge>
                              )}
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-sm">
                              <div>
                                <p className="text-gray-500">Группа кошельков</p>
                                <p className="font-medium flex items-center gap-1">
                                  <Users className="h-3 w-3" />
                                  {strategy.wallet_group_name || "Все кошельки"}
                                </p>
                                {strategy.wallet_group_name && (() => {
                                  const groupData = walletGroups.find(g => 
                                    (typeof g === 'object' ? g.group_name : g) === strategy.wallet_group_name
                                  );
                                  if (typeof groupData === 'object' && groupData) {
                                    return (
                                      <p className="text-xs text-gray-400 mt-1">
                                        {groupData.count} кошельков • {groupData.total_ton_readable?.toFixed(2) || '0.00'} TON • {groupData.total_buyt_readable?.toFixed(2) || '0.00'} {tokenSymbol || 'TOKEN'}
                                      </p>
                                    );
                                  }
                                  return null;
                                })()}
                              </div>
                              <div>
                                <p className="text-gray-500">Min Volume</p>
                                <p className="font-medium">${strategy.min_enemy_volume_usd}</p>
                              </div>
                              <div>
                                <p className="text-gray-500">Sell (Enemy Buy)</p>
                                <p className="font-medium">{strategy.enemy_buy_sell_min_percent}% - {strategy.enemy_buy_sell_max_percent}%</p>
                              </div>
                              <div>
                                <p className="text-gray-500">Buy (Enemy Sell)</p>
                                <p className="font-medium">{strategy.enemy_sell_buy_min_percent}% - {strategy.enemy_sell_buy_max_percent}%</p>
                              </div>
                            </div>

                            {/* Статистика работы стратегии */}
                            <div className="mt-4 pt-3 border-t border-slate-700">
                              <p className="text-gray-500 text-xs mb-2 flex items-center gap-1">
                                <Activity className="h-3 w-3" /> Статистика работы
                              </p>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                <div>
                                  <p className="text-gray-500">Сработала раз</p>
                                  <p className="font-semibold">{strategy.stats?.operations ?? 0}</p>
                                </div>
                                <div>
                                  <p className="text-gray-500">Купила</p>
                                  <p className="font-semibold text-green-500">
                                    {strategy.stats?.buys ?? 0}
                                    <span className="text-xs text-gray-400 ml-1">(${(strategy.stats?.buy_volume_usd ?? 0).toFixed(2)})</span>
                                  </p>
                                </div>
                                <div>
                                  <p className="text-gray-500">Продала</p>
                                  <p className="font-semibold text-red-500">
                                    {strategy.stats?.sells ?? 0}
                                    <span className="text-xs text-gray-400 ml-1">(${(strategy.stats?.sell_volume_usd ?? 0).toFixed(2)})</span>
                                  </p>
                                </div>
                                <div>
                                  <p className="text-gray-500">Последняя</p>
                                  <p className="font-medium text-xs">
                                    {strategy.stats?.last_operation ? new Date(strategy.stats.last_operation).toLocaleString() : '—'}
                                  </p>
                                </div>
                              </div>
                              {((strategy.stats?.failed ?? 0) > 0 || (strategy.stats?.blocked ?? 0) > 0 || (strategy.stats?.completed ?? 0) > 0) && (
                                <p className="text-xs text-amber-400 mt-2">
                                  Успешно: {strategy.stats?.completed ?? 0} • Ошибок: {strategy.stats?.failed ?? 0}
                                  {(strategy.stats?.blocked ?? 0) > 0 && <span className="text-blue-400"> • Заблокировано защитой: {strategy.stats?.blocked}</span>}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => toggleStrategyEnabled(strategy.id, strategy.enabled)}
                            >
                              {strategy.enabled ? "Выключить" : "Включить"}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditStrategy(strategy)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => deleteStrategy(strategy.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Strategy Dialog */}
          <Dialog open={showStrategyDialog} onOpenChange={setShowStrategyDialog}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingStrategy ? "Редактировать стратегию" : "Создать стратегию"}
                </DialogTitle>
                <DialogDescription>
                  Настройка параметров counter-trading стратегии для группы кошельков
                </DialogDescription>
              </DialogHeader>
              <StrategyForm
                strategy={editingStrategy}
                walletGroups={walletGroups}
                defaultTokenAddress={globalTokenAddress}
                tokenSymbol={tokenSymbol}
                onSubmit={(data) => {
                  if (editingStrategy) {
                    updateStrategy(editingStrategy.id, data);
                  } else {
                    createStrategy(data);
                  }
                }}
                onCancel={() => {
                  setShowStrategyDialog(false);
                  setEditingStrategy(null);
                }}
              />
            </DialogContent>
          </Dialog>

      <VerifiedOperationHistory tokenSymbol={tokenSymbol} emergency={emergency} />
        </>
      )}

      {/* Price Holding Tab */}
      {activeTab === 'priceHolding' && (
        <>
          {/* Current Price Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                Price Holding Strategy
              </CardTitle>
              <CardDescription>
                Automatically buy when price drops below floor and sell when it rises above ceiling
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <Card className="bg-blue-50">
                  <CardContent className="pt-6">
                    <p className="text-sm text-gray-600 mb-1">Current Price</p>
                    <p className="text-2xl font-bold text-blue-600">
                      ${priceHoldingStatus.current_price?.toFixed(8) || '0.00000000'}
                    </p>
                  </CardContent>
                </Card>
                <Card className={priceHoldingStatus.at_floor ? 'bg-red-50' : ''}>
                  <CardContent className="pt-6">
                    <p className="text-sm text-gray-600 mb-1">Price Floor</p>
                    <p className="text-2xl font-bold text-red-600">
                      ${priceHoldingConfig.min_price_usd?.toFixed(8)}
                    </p>
                    {priceHoldingStatus.at_floor && (
                      <Badge variant="destructive" className="mt-2">At Floor!</Badge>
                    )}
                  </CardContent>
                </Card>
                <Card className={priceHoldingStatus.at_ceiling ? 'bg-green-50' : ''}>
                  <CardContent className="pt-6">
                    <p className="text-sm text-gray-600 mb-1">Price Ceiling</p>
                    <p className="text-2xl font-bold text-green-600">
                      ${priceHoldingConfig.max_price_usd?.toFixed(8)}
                    </p>
                    {priceHoldingStatus.at_ceiling && (
                      <Badge variant="default" className="bg-green-600 mt-2">At Ceiling!</Badge>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Configuration Form */}
              <div className="space-y-6">
                {/* Enable/Disable */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium">Enable Price Holding</p>
                    <p className="text-sm text-gray-500">Automatically maintain price within range</p>
                  </div>
                  <Button
                    onClick={() => setPriceHoldingConfig({...priceHoldingConfig, enabled: !priceHoldingConfig.enabled})}
                    variant={priceHoldingConfig.enabled ? 'default' : 'outline'}
                    size="sm"
                    className={priceHoldingConfig.enabled ? 'bg-green-600' : ''}
                  >
                    {priceHoldingConfig.enabled ? 'Enabled' : 'Disabled'}
                  </Button>
                </div>

                {/* Price Floor Settings */}
                <div className="p-4 border rounded-lg">
                  <h3 className="font-semibold mb-4 flex items-center gap-2 text-red-600">
                    <TrendingDown className="h-4 w-4" />
                    Price Floor (Buy Support)
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Min Price (USD)</label>
                      <Input
                        type="number"
                        step="0.00000001"
                        value={priceHoldingConfig.min_price_usd}
                        onChange={(e) => setPriceHoldingConfig({...priceHoldingConfig, min_price_usd: parseFloat(e.target.value)})}
                        placeholder="0.00012"
                      />
                      <p className="text-xs text-gray-500 mt-1">Buy when price falls below this level</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Buy Volume (%)</label>
                      <Input
                        type="number"
                        step="1"
                        value={priceHoldingConfig.buy_volume_percent}
                        onChange={(e) => setPriceHoldingConfig({...priceHoldingConfig, buy_volume_percent: parseFloat(e.target.value)})}
                        placeholder="50"
                      />
                      <p className="text-xs text-gray-500 mt-1">% of sell volume to buy back</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Max Total Buy (USD)</label>
                      <Input
                        type="number"
                        step="10"
                        value={priceHoldingConfig.max_total_buy_usd}
                        onChange={(e) => setPriceHoldingConfig({...priceHoldingConfig, max_total_buy_usd: parseFloat(e.target.value)})}
                        placeholder="500"
                      />
                      <p className="text-xs text-gray-500 mt-1">Maximum to spend per session</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={priceHoldingConfig.stop_at_floor}
                        onChange={(e) => setPriceHoldingConfig({...priceHoldingConfig, stop_at_floor: e.target.checked})}
                        className="w-4 h-4"
                      />
                      <label className="text-sm">Stop buying if price breaks floor (protection)</label>
                    </div>
                  </div>
                </div>

                {/* Price Ceiling Settings */}
                <div className="p-4 border rounded-lg">
                  <h3 className="font-semibold mb-4 flex items-center gap-2 text-green-600">
                    <TrendingUp className="h-4 w-4" />
                    Price Ceiling (Sell at Profit)
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Max Price (USD)</label>
                      <Input
                        type="number"
                        step="0.00000001"
                        value={priceHoldingConfig.max_price_usd}
                        onChange={(e) => setPriceHoldingConfig({...priceHoldingConfig, max_price_usd: parseFloat(e.target.value)})}
                        placeholder="0.00015"
                      />
                      <p className="text-xs text-gray-500 mt-1">Start selling when price rises above</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Sell Levels</label>
                      <Input
                        type="number"
                        step="1"
                        min="1"
                        max="10"
                        value={priceHoldingConfig.sell_levels}
                        onChange={(e) => setPriceHoldingConfig({...priceHoldingConfig, sell_levels: parseInt(e.target.value)})}
                        placeholder="4"
                      />
                      <p className="text-xs text-gray-500 mt-1">Number of price levels to sell at</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Sell Volume per Level (%)</label>
                      <Input
                        type="number"
                        step="1"
                        value={priceHoldingConfig.sell_volume_percent}
                        onChange={(e) => setPriceHoldingConfig({...priceHoldingConfig, sell_volume_percent: parseFloat(e.target.value)})}
                        placeholder="25"
                      />
                      <p className="text-xs text-gray-500 mt-1">% of holdings to sell at each level</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Max Total Sell (%)</label>
                      <Input
                        type="number"
                        step="1"
                        value={priceHoldingConfig.max_total_sell_percent}
                        onChange={(e) => setPriceHoldingConfig({...priceHoldingConfig, max_total_sell_percent: parseFloat(e.target.value)})}
                        placeholder="80"
                      />
                      <p className="text-xs text-gray-500 mt-1">Maximum % of total holdings to sell</p>
                    </div>
                  </div>
                </div>

                {/* Timing Settings */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      <Clock className="h-4 w-4 inline mr-1" />
                      Check Interval (seconds)
                    </label>
                    <Input
                      type="number"
                      step="5"
                      value={priceHoldingConfig.check_interval_seconds}
                      onChange={(e) => setPriceHoldingConfig({...priceHoldingConfig, check_interval_seconds: parseFloat(e.target.value)})}
                      placeholder="30"
                    />
                    <p className="text-xs text-gray-500 mt-1">How often to check price</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      <Clock className="h-4 w-4 inline mr-1" />
                      React Delay (seconds)
                    </label>
                    <Input
                      type="number"
                      step="1"
                      value={priceHoldingConfig.react_delay_seconds}
                      onChange={(e) => setPriceHoldingConfig({...priceHoldingConfig, react_delay_seconds: parseFloat(e.target.value)})}
                      placeholder="5"
                    />
                    <p className="text-xs text-gray-500 mt-1">Delay before executing orders</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Slippage (%)
                    </label>
                    <Input
                      type="number"
                      step="0.05"
                      min="0.05"
                      max="1.0"
                      value={priceHoldingConfig.slippage_percent ?? 0.3}
                      onChange={(e) => {
                        const raw = parseFloat(e.target.value);
                        const safe = Math.max(0.05, Math.min(1.0, isNaN(raw) ? 0.3 : raw));
                        setPriceHoldingConfig({...priceHoldingConfig, slippage_percent: safe});
                      }}
                      placeholder="0.3"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Max slippage per swap. Hard cap 1.0% (≥1% = sandwich-риск). Default 0.3%.
                    </p>
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end">
                  <Button 
                    onClick={savePriceHoldingConfig}
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Save Price Holding Config
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Strategy Description */}
          <Card>
            <CardHeader>
              <CardTitle>📊 Strategy Guide</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold text-red-600 mb-2">🔻 Price Floor (Support)</h4>
                <p className="text-sm text-gray-600">
                  When token price drops below Min Price, bot automatically buys to provide support.
                  Buying stops if price breaks floor by 1% (protection against dumps).
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-green-600 mb-2">🔺 Price Ceiling (Take Profit)</h4>
                <p className="text-sm text-gray-600">
                  When price rises above Max Price, bot sells in levels. For example with 4 levels:
                  Level 1 at $0.00015 (+0%), Level 2 at +5%, Level 3 at +10%, Level 4 at +15%.
                </p>
              </div>
              <div className="bg-yellow-50 p-3 rounded">
                <p className="text-sm">
                  <strong>⚠️ Note:</strong> Price is tracked from Operation History. Make sure Counter-Trading strategy 
                  is running to collect price data, or there's recent trading activity.
                </p>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {activeTab === 'random' && (
        <div className="space-y-6">

          {/* Как это работает */}
          <div className="rounded-lg border border-blue-500/30 bg-blue-500/5 px-4 py-3 text-sm">
            <div className="font-semibold flex items-center gap-2 mb-1">
              <Zap className="h-4 w-4 text-blue-500" /> Как запустить Random &amp; Smart Pulse
            </div>
            <ul className="list-disc list-inside text-slate-500 space-y-0.5 text-xs">
              <li><b>Smart Market Making</b> — это не отдельный движок, а режим (<code>strategy_mode</code>) внутри этого модуля: random / smart_auto / manual.</li>
              <li>Включи тумблер <b>«Enabled»</b> ниже и нажми <b>«Save Random Settings»</b> — движок поднимется автоматически (или включи модуль в пульте сверху).</li>
              <li>Текущее состояние и статус серии видны в пульте управления и в карточке статуса ниже.</li>
            </ul>
          </div>

          {/* Smart Position Status */}
          {randomConfig?.position_tracking_enabled !== false && (
            <Card className="border-2 border-blue-500/30">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    📊 Smart Position
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      randomStatus?.net_position_percent >= 0 
                        ? 'bg-emerald-500/20 text-emerald-400' 
                        : 'bg-red-500/20 text-red-400'
                    }`}>
                      {(randomStatus?.net_position_percent || 0) >= 0 ? '+' : ''}{(randomStatus?.net_position_percent || 0).toFixed(1)}%
                    </span>
                    {(() => {
                      const pct = Math.abs(randomStatus?.net_position_percent || 0) / 100;
                      const soft = randomConfig?.soft_rebalance_threshold || 0.3;
                      const hard = randomConfig?.hard_rebalance_threshold || 0.6;
                      const crit = randomConfig?.critical_rebalance_threshold || 0.8;
                      const zone = pct >= crit
                        ? { l: 'CRITICAL', c: 'bg-red-500/20 text-red-400' }
                        : pct >= hard
                          ? { l: 'HARD', c: 'bg-orange-500/20 text-orange-400' }
                          : pct >= soft
                            ? { l: 'SOFT', c: 'bg-amber-500/20 text-amber-400' }
                            : { l: 'NEUTRAL', c: 'bg-emerald-500/20 text-emerald-400' };
                      return <span className={`text-xs px-2 py-0.5 rounded ${zone.c}`}>Зона: {zone.l}</span>;
                    })()}
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-bold ${
                      (randomStatus?.net_position_usd || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'
                    }`}>
                      ${(randomStatus?.net_position_usd || 0).toFixed(2)}
                    </span>
                    <button
                      onClick={async () => {
                        if (confirm('Сбросить позицию на $0?')) {
                          try {
                            await axios.post(`${API_URL}/api/market-maker/smart-position/reset`);
                            loadRandomStatus();
                          } catch (e) {
                            console.error('Reset error:', e);
                          }
                        }
                      }}
                      className="text-xs bg-slate-700 hover:bg-slate-600 px-2 py-1 rounded"
                    >
                      Reset
                    </button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-2">
                {/* Position Bar */}
                <div className="mb-4">
                  <div className="h-6 bg-slate-700 rounded-full overflow-hidden relative">
                    {/* Center marker */}
                    <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-white/50 z-10"></div>
                    {/* Position indicator */}
                    <div 
                      className={`absolute top-0 bottom-0 transition-all duration-300 ${
                        (randomStatus?.net_position_percent || 0) >= 0 
                          ? 'bg-emerald-500 left-1/2' 
                          : 'bg-red-500 right-1/2'
                      }`}
                      style={{
                        width: `${Math.min(Math.abs(randomStatus?.net_position_percent || 0), 100) / 2}%`
                      }}
                    ></div>
                    {/* Zone markers */}
                    <div className="absolute top-0 bottom-0 left-[15%] w-px bg-yellow-500/30"></div>
                    <div className="absolute top-0 bottom-0 left-[35%] w-px bg-orange-500/30"></div>
                    <div className="absolute top-0 bottom-0 right-[15%] w-px bg-yellow-500/30"></div>
                    <div className="absolute top-0 bottom-0 right-[35%] w-px bg-orange-500/30"></div>
                    {/* Zone labels */}
                    <div className="absolute inset-0 flex items-center justify-between px-2 text-[9px] text-slate-400">
                      <span>SELL</span>
                      <span>NEUTRAL</span>
                      <span>BUY</span>
                    </div>
                  </div>
                  <div className="flex justify-between text-xs text-slate-500 mt-1">
                    <span>-100%</span>
                    <span className="text-slate-400">Max: ${randomConfig?.max_net_position_usd || 500}</span>
                    <span>+100%</span>
                  </div>
                </div>

                {/* Smart State */}
                {randomStatus?.smart_state && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div className="bg-slate-800 rounded p-2">
                      <div className="text-slate-500 text-xs">Режим</div>
                      <div className="text-white capitalize">{randomConfig?.strategy_mode || 'smart_auto'}</div>
                    </div>
                    <div className="bg-slate-800 rounded p-2">
                      <div className="text-slate-500 text-xs">Текущая стратегия</div>
                      <div className="text-white">{randomStatus?.smart_state?.current_strategy_id || '—'}</div>
                    </div>
                    <div className="bg-slate-800 rounded p-2">
                      <div className="text-slate-500 text-xs">Buy в серии</div>
                      <div className="text-emerald-400">${(randomStatus?.smart_state?.accumulated_buy_usd || 0).toFixed(2)}</div>
                    </div>
                    <div className="bg-slate-800 rounded p-2">
                      <div className="text-slate-500 text-xs">Sell в серии</div>
                      <div className="text-red-400">${(randomStatus?.smart_state?.accumulated_sell_usd || 0).toFixed(2)}</div>
                    </div>
                  </div>
                )}

                {/* Strategy reason */}
                {randomStatus?.smart_state?.strategy_reason && (
                  <div className="mt-3 p-2 bg-blue-900/20 border border-blue-500/30 rounded text-xs text-blue-300">
                    🧠 {randomStatus.smart_state.strategy_reason}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
          
          <Card>
            <CardHeader>
              <CardTitle>Smart Market Making</CardTitle>
              <CardDescription>
                Интеллектуальная система маркетмейкинга с учётом позиции и истории
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3 mb-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="accent-blue-600"
                    checked={randomConfig?.enabled || false}
                    onChange={(e) => {
                      if (!randomConfig) return;
                      setRandomConfig({ ...randomConfig, enabled: e.target.checked });
                    }}
                  />
                  Enabled
                </label>
                <span className="text-xs text-gray-500">
                  {randomStatus?.status === 'running' ? 'Currently running' : 'Idle'}
                </span>
              </div>

              {randomLoading && !randomConfig ? (
                <p className="text-xs text-gray-500">Loading random configuration...</p>
              ) : (
                randomConfig && (
                  <div className="space-y-6">
                    {/* Основные настройки */}
                    <div>
                      <h4 className="text-sm font-medium text-slate-300 mb-3">Основные параметры</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <label className="text-xs text-gray-500 flex flex-col gap-1">
                          Интервал мин (с)
                          <Input type="number" value={randomConfig.interval_min_seconds}
                            onChange={(e) => setRandomConfig({...randomConfig, interval_min_seconds: Number(e.target.value)})} />
                        </label>
                        <label className="text-xs text-gray-500 flex flex-col gap-1">
                          Интервал макс (с)
                          <Input type="number" value={randomConfig.interval_max_seconds}
                            onChange={(e) => setRandomConfig({...randomConfig, interval_max_seconds: Number(e.target.value)})} />
                        </label>
                        <label className="text-xs text-gray-500 flex flex-col gap-1">
                          Шагов мин
                          <Input type="number" value={randomConfig.steps_min}
                            onChange={(e) => setRandomConfig({...randomConfig, steps_min: Number(e.target.value)})} />
                        </label>
                        <label className="text-xs text-gray-500 flex flex-col gap-1">
                          Шагов макс
                          <Input type="number" value={randomConfig.steps_max}
                            onChange={(e) => setRandomConfig({...randomConfig, steps_max: Number(e.target.value)})} />
                        </label>
                        <label className="text-xs text-gray-500 flex flex-col gap-1">
                          Объём мин (USD)
                          <Input type="number" value={randomConfig.volume_min_usd}
                            onChange={(e) => setRandomConfig({...randomConfig, volume_min_usd: Number(e.target.value)})} />
                        </label>
                        <label className="text-xs text-gray-500 flex flex-col gap-1">
                          Объём макс (USD)
                          <Input type="number" value={randomConfig.volume_max_usd}
                            onChange={(e) => setRandomConfig({...randomConfig, volume_max_usd: Number(e.target.value)})} />
                        </label>
                        <label className="text-xs text-gray-500 flex flex-col gap-1">
                          Trend Bias (%)
                          <Input type="number" value={randomConfig.trend_bias_percent}
                            onChange={(e) => setRandomConfig({...randomConfig, trend_bias_percent: Number(e.target.value)})} />
                        </label>
                        <label className="text-xs text-gray-500 flex flex-col gap-1">
                          Порог разделения (USD)
                          <Input type="number" value={randomConfig.split_threshold_usd || 50}
                            onChange={(e) => setRandomConfig({...randomConfig, split_threshold_usd: Number(e.target.value)})} />
                        </label>
                      </div>
                    </div>

                    {/* Ценовые ограничения */}
                    <div>
                      <h4 className="text-sm font-medium text-slate-300 mb-3">Ценовые ограничения</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <label className="text-xs text-gray-500 flex flex-col gap-1">
                          Мин цена (USD)
                          <Input type="number" step="0.00000001" value={randomConfig.min_price_usd || 0}
                            onChange={(e) => setRandomConfig({...randomConfig, min_price_usd: Number(e.target.value)})} />
                          <span className="text-xs text-slate-500">0 = без ограничения</span>
                        </label>
                        <label className="text-xs text-gray-500 flex flex-col gap-1">
                          Макс цена (USD)
                          <Input type="number" step="0.00000001" value={randomConfig.max_price_usd || 0}
                            onChange={(e) => setRandomConfig({...randomConfig, max_price_usd: Number(e.target.value)})} />
                          <span className="text-xs text-slate-500">0 = без ограничения</span>
                        </label>
                        <label className="text-xs text-gray-500 flex flex-col gap-1 col-span-2">
                          <span className="flex items-center gap-2 mt-4">
                            <input type="checkbox" className="accent-blue-600"
                              checked={randomConfig.abort_on_price_exit !== false}
                              onChange={(e) => setRandomConfig({...randomConfig, abort_on_price_exit: e.target.checked})} />
                            Остановить серию при выходе цены
                          </span>
                        </label>
                      </div>
                    </div>

                    {/* Безопасность */}
                    <div>
                      <h4 className="text-sm font-medium text-slate-300 mb-3">Безопасность</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <label className="text-xs text-gray-500 flex flex-col gap-1">
                          Макс объём/день (USD)
                          <Input type="number" value={randomConfig.max_daily_volume_usd || 0}
                            onChange={(e) => setRandomConfig({...randomConfig, max_daily_volume_usd: Number(e.target.value)})} />
                          <span className="text-xs text-slate-500">0 = без ограничения</span>
                        </label>
                        <label className="text-xs text-gray-500 flex flex-col gap-1">
                          Макс неудач подряд
                          <Input type="number" value={randomConfig.max_consecutive_failures || 3}
                            onChange={(e) => setRandomConfig({...randomConfig, max_consecutive_failures: Number(e.target.value)})} />
                        </label>
                        <label className="text-xs text-gray-500 flex flex-col gap-1">
                          Cooldown (с)
                          <Input type="number" value={randomConfig.cooldown_after_failures_seconds || 300}
                            onChange={(e) => setRandomConfig({...randomConfig, cooldown_after_failures_seconds: Number(e.target.value)})} />
                        </label>
                        <label className="text-xs text-gray-500 flex flex-col gap-1">
                          Мин TON на кошельке
                          <Input type="number" step="0.1" value={randomConfig.min_wallet_balance_ton || 0.5}
                            onChange={(e) => setRandomConfig({...randomConfig, min_wallet_balance_ton: Number(e.target.value)})} />
                          <span className="text-xs text-slate-500">Нужно ≥0.5 для SELL</span>
                        </label>
                        <label className="text-xs text-gray-500 flex flex-col gap-1">
                          Интервал между шагами (мин, сек)
                          <div className="flex gap-2">
                            <Input type="number" className="w-20" value={randomConfig.step_interval_min_seconds || 5}
                              onChange={(e) => setRandomConfig({...randomConfig, step_interval_min_seconds: Number(e.target.value)})} />
                            <Input type="number" className="w-20" value={randomConfig.step_interval_max_seconds || 15}
                              onChange={(e) => setRandomConfig({...randomConfig, step_interval_max_seconds: Number(e.target.value)})} />
                          </div>
                        </label>
                        <label className="text-xs text-gray-500 flex flex-col gap-1 col-span-2">
                          <span className="flex items-center gap-2">
                            <input type="checkbox" className="accent-blue-600"
                              checked={randomConfig.require_balance_check !== false}
                              onChange={(e) => setRandomConfig({...randomConfig, require_balance_check: e.target.checked})} />
                            Проверять балансы перед серией
                          </span>
                        </label>
                      </div>
                    </div>

                    {/* Smart Market Making Settings */}
                    <div className="border-t border-slate-700 pt-4">
                      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                        <h4 className="text-sm font-medium text-blue-400">🧠 Smart Market Making</h4>
                        <Button
                          size="sm"
                          onClick={applySmartAutoPreset}
                          disabled={randomSaving || !randomConfig}
                          className="bg-blue-600 hover:bg-blue-700 h-8"
                        >
                          <Zap className="h-3.5 w-3.5 mr-1.5" /> Smart Auto preset
                        </Button>
                      </div>

                      {/* Легенда зон позиции — как движок принимает решения */}
                      <div className="rounded-lg border border-slate-700 bg-slate-800/40 p-3 mb-4 text-xs">
                        <div className="text-slate-300 mb-2">
                          Автопилот держит позицию (buy − sell) у нуля. Чем дальше позиция от нуля — тем строже выравнивание:
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          <div className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-500" /> <b>Neutral</b> &lt;30% — свободный выбор по тренду</div>
                          <div className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-amber-500" /> <b>Soft</b> 30–60% — перекос к балансу</div>
                          <div className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-orange-500" /> <b>Hard</b> 60–80% — только обратные сделки</div>
                          <div className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-red-500" /> <b>Critical</b> ≥80% — аварийный ребаланс</div>
                        </div>
                        <div className="text-slate-500 mt-2">
                          % считается от «Макс позиция (USD)». Для автономной работы: режим <b>Smart Auto</b> + отслеживание позиции + выбранная группа кошельков.
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <label className="text-xs text-gray-500 flex flex-col gap-1">
                          Режим стратегии
                          <select 
                            className="bg-slate-800 border border-slate-600 rounded p-2 text-white text-sm"
                            value={randomConfig.strategy_mode || 'smart_auto'}
                            onChange={(e) => setRandomConfig({...randomConfig, strategy_mode: e.target.value})}
                          >
                            <option value="random">Random (legacy)</option>
                            <option value="smart_auto">Smart Auto</option>
                            <option value="manual">Manual</option>
                          </select>
                        </label>
                        <label className="text-xs text-gray-500 flex flex-col gap-1">
                          Макс позиция (USD)
                          <Input type="number" value={randomConfig.max_net_position_usd || 500}
                            onChange={(e) => setRandomConfig({...randomConfig, max_net_position_usd: Number(e.target.value)})} />
                          <span className="text-xs text-slate-500">Лимит buy - sell</span>
                        </label>
                        <label className="text-xs text-gray-500 flex flex-col gap-1">
                          Soft Rebalance (%)
                          <Input type="number" step="0.1" min="0" max="1" value={randomConfig.soft_rebalance_threshold || 0.3}
                            onChange={(e) => setRandomConfig({...randomConfig, soft_rebalance_threshold: Number(e.target.value)})} />
                          <span className="text-xs text-slate-500">Bias к балансировке</span>
                        </label>
                        <label className="text-xs text-gray-500 flex flex-col gap-1">
                          Hard Rebalance (%)
                          <Input type="number" step="0.1" min="0" max="1" value={randomConfig.hard_rebalance_threshold || 0.6}
                            onChange={(e) => setRandomConfig({...randomConfig, hard_rebalance_threshold: Number(e.target.value)})} />
                          <span className="text-xs text-slate-500">Только балансировка</span>
                        </label>
                        <label className="text-xs text-gray-500 flex flex-col gap-1">
                          Critical Rebalance (%)
                          <Input type="number" step="0.1" min="0" max="1" value={randomConfig.critical_rebalance_threshold || 0.8}
                            onChange={(e) => setRandomConfig({...randomConfig, critical_rebalance_threshold: Number(e.target.value)})} />
                          <span className="text-xs text-slate-500">Emergency mode</span>
                        </label>
                        <label className="text-xs text-gray-500 flex flex-col gap-1">
                          История (серий)
                          <Input type="number" value={randomConfig.history_lookback_series || 5}
                            onChange={(e) => setRandomConfig({...randomConfig, history_lookback_series: Number(e.target.value)})} />
                          <span className="text-xs text-slate-500">Для выбора стратегии</span>
                        </label>
                        <label className="text-xs text-gray-500 flex flex-col gap-1">
                          Макс подряд в направлении
                          <Input type="number" value={randomConfig.max_consecutive_same_direction || 3}
                            onChange={(e) => setRandomConfig({...randomConfig, max_consecutive_same_direction: Number(e.target.value)})} />
                        </label>
                        <label className="text-xs text-gray-500 flex flex-col gap-1">
                          Balance Ratio
                          <Input type="number" step="0.05" min="0.5" max="1" value={randomConfig.series_balance_ratio || 0.85}
                            onChange={(e) => setRandomConfig({...randomConfig, series_balance_ratio: Number(e.target.value)})} />
                          <span className="text-xs text-slate-500">85% = 85% объёма</span>
                        </label>
                        <label className="text-xs text-gray-500 flex flex-col gap-1 col-span-2">
                          <span className="flex items-center gap-2">
                            <input type="checkbox" className="accent-blue-600"
                              checked={randomConfig.position_tracking_enabled !== false}
                              onChange={(e) => setRandomConfig({...randomConfig, position_tracking_enabled: e.target.checked})} />
                            Включить отслеживание позиции
                          </span>
                        </label>
                      </div>
                    </div>

                    {/* Rate limiting */}
                    <div>
                      <h4 className="text-sm font-medium text-slate-300 mb-3">Ограничения скорости</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <label className="text-xs text-gray-500 flex flex-col gap-1">
                          Макс операций/мин
                          <Input type="number" value={randomConfig.max_ops_per_minute}
                            onChange={(e) => setRandomConfig({...randomConfig, max_ops_per_minute: Number(e.target.value)})} />
                        </label>
                        <label className="text-xs text-gray-500 flex flex-col gap-1">
                          Макс серий одновременно
                          <Input type="number" value={randomConfig.max_concurrent_series}
                            onChange={(e) => setRandomConfig({...randomConfig, max_concurrent_series: Number(e.target.value)})} />
                        </label>
                      </div>
                    </div>

                    {/* Группы кошельков */}
                    <div>
                      <h4 className="text-sm font-medium text-slate-300 mb-3">Группы кошельков</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <label className="text-xs text-gray-500 flex flex-col gap-1">
                          Основная группа
                          <select className="rounded border border-slate-700 bg-slate-900 text-white p-2 text-sm"
                            value={randomConfig.primary_wallet_group || ''}
                            onChange={(e) => setRandomConfig({...randomConfig, primary_wallet_group: e.target.value || null})}>
                            <option value="">Все кошельки</option>
                            {walletGroups.map((group) => {
                              const name = typeof group === 'object' ? group.group_name : group;
                              return <option key={`group-${name}`} value={name}>{name}</option>;
                            })}
                          </select>
                        </label>
                        <label className="text-xs text-gray-500 flex flex-col gap-1">
                          Группа реакции
                          <select className="rounded border border-slate-700 bg-slate-900 text-white p-2 text-sm"
                            value={randomConfig.reaction_wallet_group || ''}
                            onChange={(e) => setRandomConfig({...randomConfig, reaction_wallet_group: e.target.value || null})}>
                            <option value="">Использовать основную</option>
                            {walletGroups.map((group) => {
                              const name = typeof group === 'object' ? group.group_name : group;
                              return <option key={`reaction-${name}`} value={name}>{name}</option>;
                            })}
                          </select>
                        </label>
                      </div>
                    </div>

                    {/* Smart Strategies Overview */}
                    {randomConfig.strategy_mode !== 'random' && (
                      <div className="border-t border-gray-600 pt-4">
                        <h4 className="text-base font-semibold text-white mb-4">📋 Доступные стратегии</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {[
                            { id: 'pump_then_balance', name: 'Pump → Balance', direction: 'bullish', effect: 'slight_long', 
                              desc: 'Мелкие покупки → крупная продажа', color: 'green' },
                            { id: 'dump_then_balance', name: 'Dump → Balance', direction: 'bearish', effect: 'slight_short',
                              desc: 'Мелкие продажи → крупная покупка', color: 'red' },
                            { id: 'accumulation_stealth', name: 'Stealth Accumulation', direction: 'bullish', effect: 'long',
                              desc: 'Скрытое накопление с маскировкой', color: 'green' },
                            { id: 'distribution_stealth', name: 'Stealth Distribution', direction: 'bearish', effect: 'short',
                              desc: 'Скрытая разгрузка с маскировкой', color: 'red' },
                            { id: 'wash_neutral', name: 'Wash Trading', direction: 'neutral', effect: 'neutral',
                              desc: 'Создание объёма без изменения позиции', color: 'blue' },
                            { id: 'momentum_long', name: 'Momentum Long', direction: 'bullish', effect: 'long',
                              desc: 'Следование восходящему тренду', color: 'green' },
                            { id: 'momentum_short', name: 'Momentum Short', direction: 'bearish', effect: 'short',
                              desc: 'Следование нисходящему тренду', color: 'red' },
                            { id: 'rebalance_buy', name: 'Emergency BUY', direction: 'bullish', effect: 'strong_long',
                              desc: 'Экстренная покупка для баланса', color: 'yellow' },
                            { id: 'rebalance_sell', name: 'Emergency SELL', direction: 'bearish', effect: 'strong_short',
                              desc: 'Экстренная продажа для баланса', color: 'yellow' },
                          ].map((strategy) => (
                            <div 
                              key={strategy.id}
                              className={`p-4 rounded-lg border-2 transition-all ${
                                randomConfig.current_strategy === strategy.id 
                                  ? 'border-blue-500 bg-blue-900/40' 
                                  : 'border-gray-600 bg-gray-800 hover:border-gray-500'
                              } ${randomConfig.strategy_mode === 'manual' ? 'cursor-pointer' : ''}`}
                              onClick={() => {
                                if (randomConfig.strategy_mode === 'manual') {
                                  setRandomConfig({...randomConfig, current_strategy: strategy.id});
                                }
                              }}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-semibold text-base text-white">{strategy.name}</span>
                                <span className={`text-xs px-2 py-1 rounded font-medium ${
                                  strategy.direction === 'bullish' ? 'bg-green-600 text-white' :
                                  strategy.direction === 'bearish' ? 'bg-red-600 text-white' :
                                  'bg-blue-600 text-white'
                                }`}>
                                  {strategy.direction}
                                </span>
                              </div>
                              <div className="text-sm text-gray-300 mb-1">{strategy.desc}</div>
                              <div className="text-sm text-gray-400">Effect: {strategy.effect}</div>
                            </div>
                          ))}
                        </div>
                        {randomConfig.strategy_mode === 'manual' && (
                          <div className="mt-3 text-sm text-gray-400">
                            💡 В Manual режиме выберите стратегию кликом. В Smart Auto режиме система выбирает автоматически.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              )}

              <div className="mt-4 flex items-center gap-3">
                <Button onClick={saveRandomConfig} disabled={randomSaving || !randomConfig}>
                  {randomSaving ? 'Saving...' : 'Save Random Settings'}
                </Button>
                <Button
                  variant="outline"
                  onClick={loadRandomConfig}
                  disabled={randomLoading}
                >
                  Reload
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Статус текущей серии - расширенный */}
          <Card className="border-l-4 border-l-blue-500">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center justify-between">
                <span>Текущая серия</span>
                <div className="flex items-center gap-2">
                  <Badge
                    className={`px-3 py-1 ${
                      randomStatus?.status === 'running' ? 'bg-emerald-500 text-white' :
                      randomStatus?.status === 'waiting' ? 'bg-yellow-500 text-black' :
                      randomStatus?.status === 'cooldown' ? 'bg-orange-500 text-white' :
                      randomStatus?.status === 'waiting_price' ? 'bg-purple-500 text-white' :
                      randomStatus?.status === 'daily_limit' ? 'bg-red-500 text-white' :
                      'bg-slate-700 text-slate-300'
                    }`}
                  >
                    {randomStatus?.status || 'idle'}
                  </Badge>
                  {randomStatus?.can_abort && (
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={async () => {
                        try {
                          await axios.post(`${API_URL}/api/market-maker/random-abort`);
                          loadRandomStatus();
                          toast.success('Запрос на остановку отправлен');
                        } catch (e) {
                          toast.error('Ошибка остановки серии');
                        }
                      }}
                    >
                      Остановить
                    </Button>
                  )}
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {randomStatus?.status === 'running' && randomStatus?.planned_steps?.length > 0 ? (
                <div className="space-y-4">
                  {/* Прогресс */}
                  <div className="flex items-center gap-3">
                    <div className="flex-1 bg-slate-800 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full transition-all"
                        style={{
                          width: `${((randomStatus.current_step_index || 0) / randomStatus.total_steps) * 100}%`
                        }}
                      />
                    </div>
                    <span className="text-sm text-slate-400">
                      {randomStatus.current_step_index || 0}/{randomStatus.total_steps}
                    </span>
                  </div>
                  
                  {/* Информация о серии */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div className="bg-slate-800 rounded p-2">
                      <div className="text-slate-500 text-xs">Шаблон</div>
                      <div className="text-white">{randomStatus.template}</div>
                    </div>
                    <div className="bg-slate-800 rounded p-2">
                      <div className="text-slate-500 text-xs">Текущий шаг</div>
                      <div className="text-white">{randomStatus.current_step || '—'}</div>
                    </div>
                    <div className="bg-slate-800 rounded p-2">
                      <div className="text-slate-500 text-xs">Объём</div>
                      <div className="text-white">${(randomStatus.current_volume_usd || 0).toFixed(2)}</div>
                    </div>
                    <div className="bg-slate-800 rounded p-2">
                      <div className="text-slate-500 text-xs">Цена</div>
                      <div className="text-white">${(randomStatus.current_price || 0).toFixed(8)}</div>
                    </div>
                  </div>
                  
                  {/* Сводка по плану: Buy vs Sell */}
                  {(() => {
                    const steps = randomStatus.planned_steps || [];
                    const buyTotal = steps.filter(s => s.direction === 'buy').reduce((sum, s) => sum + (s.volume_usd || 0), 0);
                    const sellTotal = steps.filter(s => s.direction === 'sell').reduce((sum, s) => sum + (s.volume_usd || 0), 0);
                    const buyCount = steps.filter(s => s.direction === 'buy').length;
                    const sellCount = steps.filter(s => s.direction === 'sell').length;
                    return (
                      <div className="grid grid-cols-2 gap-3 mt-3">
                        <div className="bg-emerald-100 dark:bg-emerald-900/30 border border-emerald-400 dark:border-emerald-700 rounded p-2">
                          <div className="text-emerald-700 dark:text-emerald-400 text-xs font-medium">🟢 ПОКУПКА</div>
                          <div className="text-emerald-800 dark:text-emerald-300 text-lg font-bold">${buyTotal.toFixed(2)}</div>
                          <div className="text-emerald-600 dark:text-emerald-500 text-xs">{buyCount} операций</div>
                        </div>
                        <div className="bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-700 rounded p-2">
                          <div className="text-red-700 dark:text-red-400 text-xs font-medium">🔴 ПРОДАЖА</div>
                          <div className="text-red-800 dark:text-red-300 text-lg font-bold">${sellTotal.toFixed(2)}</div>
                          <div className="text-red-600 dark:text-red-500 text-xs">{sellCount} операций</div>
                        </div>
                      </div>
                    );
                  })()}
                  
                  {/* План серии */}
                  <div className="mt-3">
                    <div className="text-xs text-slate-500 mb-2">План серии:</div>
                    <div className="flex flex-wrap gap-1">
                      {randomStatus.planned_steps.map((step, idx) => {
                        const isExecuted = idx < (randomStatus.current_step_index || 0);
                        const isCurrent = idx === (randomStatus.current_step_index || 0);
                        const tonPrice = randomStatus.current_price ? (step.volume_usd / (randomStatus.current_price * 14000)) : 0; // примерный расчёт TON
                        const tonAmount = step.volume_usd && randomStatus.config?.ton_price_usd 
                          ? (step.volume_usd / randomStatus.config.ton_price_usd).toFixed(2)
                          : (step.volume_usd / 1.6).toFixed(2); // fallback ~$1.6/TON
                        return (
                          <div
                            key={idx}
                            className={`px-2 py-1 rounded text-xs ${
                              isExecuted ? 'bg-emerald-700 text-white' :
                              isCurrent ? 'bg-blue-600 text-white animate-pulse' :
                              'bg-slate-700 text-slate-400'
                            }`}
                            title={`${step.direction?.toUpperCase()} ${step.size} - $${step.volume_usd?.toFixed(2)} (~${tonAmount} TON)`}
                          >
                            {step.direction === 'buy' ? '🟢' : step.direction === 'pause' ? '⏸️' : '🔴'} {step.label || step.direction} (${step.volume_usd?.toFixed(1)} / {tonAmount}T)
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between text-sm text-slate-400">
                  <div>
                    {randomStatus?.status === 'waiting' && randomStatus?.next_run_at && (
                      <>Следующий запуск: {new Date(randomStatus.next_run_at).toLocaleTimeString()}</>
                    )}
                    {randomStatus?.status === 'cooldown' && (
                      <>На паузе после ошибок ({Math.round(randomStatus.cooldown_remaining || 0)}s)</>
                    )}
                    {randomStatus?.status === 'waiting_price' && (
                      <>Ожидание входа цены в диапазон</>
                    )}
                    {randomStatus?.status === 'daily_limit' && (
                      <>Дневной лимит объёма достигнут (${(randomStatus.daily_volume_usd || 0).toFixed(2)})</>
                    )}
                    {randomStatus?.status === 'idle' && <>Готов к запуску</>}
                    {randomStatus?.status === 'disabled' && <>Модуль отключён</>}
                  </div>
                  <div className="text-xs">
                    Неудач подряд: {randomStatus?.consecutive_failures || 0} |
                    Дневной объём: ${(randomStatus?.daily_volume_usd || 0).toFixed(2)}
                  </div>
                </div>
              )}

              {/* Capacity Monitor - Balance Preparation Engine */}
              {capacityStatus && (
                <div className="mt-3 p-3 bg-slate-900 rounded-lg border border-slate-500">
                  <div className="flex justify-between items-center mb-3">
                    <div className="text-sm font-bold text-white">📊 Capacity Monitor</div>
                    {capacityStatus.needs_preparation && (
                      <span className="text-xs px-2 py-1 bg-orange-600 text-white font-medium rounded">
                        🔧 Prep: {capacityStatus.recommended_prep || 'needed'}
                      </span>
                    )}
                  </div>
                  
                  {/* BUY Capacity */}
                  <div className="mb-3">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-white font-medium">BUY Capacity</span>
                      <span className={`font-bold ${
                        capacityStatus.buy?.status === 'critical' ? 'text-red-400' :
                        capacityStatus.buy?.status === 'low' ? 'text-yellow-300' :
                        capacityStatus.buy?.status === 'ok' ? 'text-green-400' :
                        'text-cyan-300'
                      }`}>
                        {capacityStatus.buy?.relative?.toFixed(0) || 0}% (${capacityStatus.buy?.absolute?.toFixed(0) || 0})
                      </span>
                    </div>
                    <div className="w-full h-3 bg-slate-700 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all duration-500 ${
                          capacityStatus.buy?.status === 'critical' ? 'bg-red-500' :
                          capacityStatus.buy?.status === 'low' ? 'bg-yellow-400' :
                          capacityStatus.buy?.status === 'ok' ? 'bg-green-500' :
                          'bg-cyan-400'
                        }`}
                        style={{ width: `${Math.min(capacityStatus.buy?.relative || 0, 100)}%` }}
                      />
                    </div>
                  </div>
                  
                  {/* SELL Capacity */}
                  <div className="mb-3">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-white font-medium">SELL Capacity</span>
                      <span className={`font-bold ${
                        capacityStatus.sell?.status === 'critical' ? 'text-red-400' :
                        capacityStatus.sell?.status === 'low' ? 'text-yellow-300' :
                        capacityStatus.sell?.status === 'ok' ? 'text-green-400' :
                        'text-cyan-300'
                      }`}>
                        {capacityStatus.sell?.relative?.toFixed(0) || 0}% ({capacityStatus.sell?.ready_count || 0}/{capacityStatus.sell?.total_count || 0} кош.)
                      </span>
                    </div>
                    <div className="w-full h-3 bg-slate-700 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all duration-500 ${
                          capacityStatus.sell?.status === 'critical' ? 'bg-red-500' :
                          capacityStatus.sell?.status === 'low' ? 'bg-yellow-400' :
                          capacityStatus.sell?.status === 'ok' ? 'bg-green-500' :
                          'bg-cyan-400'
                        }`}
                        style={{ width: `${Math.min(capacityStatus.sell?.relative || 0, 100)}%` }}
                      />
                    </div>
                  </div>
                  
                  {/* Wallet Breakdown */}
                  <div className="flex flex-wrap gap-3 text-sm text-white mt-2 p-2 bg-slate-800 rounded">
                    <span>🏦 TON-rich: <span className="text-cyan-300 font-medium">{capacityStatus.wallet_breakdown?.ton_rich || 0}</span></span>
                    <span>📭 Empty: <span className="text-blue-300 font-medium">{capacityStatus.wallet_breakdown?.empty_wallets || 0}</span></span>
                    <span>⛽ Need gas: <span className="text-orange-300 font-medium">{capacityStatus.wallet_breakdown?.gas_needed || 0}</span></span>
                  </div>
                  
                  {/* Prep Status */}
                  {prepStatus && prepStatus.prep_series_count > 0 && (
                    <div className="mt-2 text-sm text-white border-t border-slate-600 pt-2">
                      Prep серий: <span className="text-cyan-300 font-medium">{prepStatus.prep_series_count}/2</span> | 
                      Последний: <span className="text-green-300 font-medium">{prepStatus.last_prep_type || '-'}</span>
                    </div>
                  )}
                </div>
              )}
              
              {/* Доступные балансы (legacy view) */}
              {randomStatus?.available_balances && !capacityStatus && (
                <div className="mt-3 p-2 bg-slate-800/50 rounded border border-slate-700">
                  <div className="text-xs text-slate-400 mb-1">Доступные средства:</div>
                  <div className="flex flex-wrap gap-3 text-xs">
                    <span className="text-white">
                      Кошельков: <span className="text-emerald-400">{randomStatus.available_balances.wallets_available || 0}</span>/{randomStatus.available_balances.wallets_total || 0}
                      {randomStatus.available_balances.wallets_locked > 0 && (
                        <span className="text-yellow-400 ml-1">(🔒{randomStatus.available_balances.wallets_locked})</span>
                      )}
                    </span>
                    <span className="text-white">
                      TON: <span className="text-cyan-400">{randomStatus.available_balances.ton_available || 0}</span> (~${randomStatus.available_balances.usd_available_for_buy || 0})
                    </span>
                    <span className="text-white">
                      SELL: {randomStatus.available_balances.wallets_ready_for_sell > 0 
                        ? <span className="text-emerald-400">✓ {randomStatus.available_balances.wallets_ready_for_sell} кош.</span>
                        : <span className="text-red-400">✗ нет</span>}
                    </span>
                  </div>
                  {randomStatus.available_balances.wallets_ready_for_sell === 0 && randomStatus.available_balances.wallets_with_tokens > 0 && (
                    <div className="text-yellow-400 text-xs mt-1">
                      ⚠️ {randomStatus.available_balances.wallets_with_tokens} кош. с токенами, но TON менее 0.5 для газа SELL
                    </div>
                  )}
                  {randomStatus.available_balances.error && (
                    <div className="text-red-400 text-xs mt-1">⚠️ {randomStatus.available_balances.error}</div>
                  )}
                </div>
              )}
              
              {/* Последняя серия */}
              {randomStatus?.last_series_summary && (
                <div className="mt-4 p-3 bg-slate-800 rounded">
                  <div className="text-xs text-slate-400 mb-1">Последняя серия</div>
                  <div className="flex flex-wrap gap-3 text-sm text-slate-200">
                    <span className={randomStatus.last_series_summary.status === 'completed' ? 'text-emerald-400 font-medium' : 'text-red-400 font-medium'}>
                      {randomStatus.last_series_summary.status}
                    </span>
                    <span className="text-white">{randomStatus.last_series_summary.template}</span>
                    <span className="text-slate-300">{randomStatus.last_series_summary.successes}/{randomStatus.last_series_summary.operations} ops</span>
                    <span className="text-cyan-400">${(randomStatus.last_series_summary.volume_usd || 0).toFixed(2)}</span>
                    {randomStatus.last_series_summary.price_change_percent !== undefined && (
                      <span className={randomStatus.last_series_summary.price_change_percent >= 0 ? 'text-emerald-400 font-medium' : 'text-red-400 font-medium'}>
                        {randomStatus.last_series_summary.price_change_percent >= 0 ? '+' : ''}
                        {randomStatus.last_series_summary.price_change_percent.toFixed(2)}%
                      </span>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Market Analysis */}
          <Card className="border border-purple-500/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                📊 Анализ рынка
                <span className="text-xs text-slate-400 font-normal">обновление каждые 30 сек</span>
              </CardTitle>
              <CardDescription>Тренд по нескольким таймфреймам для выбора стратегии</CardDescription>
            </CardHeader>
            <CardContent>
              <MarketAnalysisCard />
            </CardContent>
          </Card>

          {/* Smart Series History */}
          <Card>
            <CardHeader>
              <CardTitle>🧠 Smart Series History</CardTitle>
              <CardDescription>История выполненных smart-стратегий с фильтрацией</CardDescription>
            </CardHeader>
            <CardContent>
              <SmartSeriesHistory />
            </CardContent>
          </Card>

          {/* Legacy Random Pulse History */}
          <Card>
            <CardHeader>
              <CardTitle>История серий (Legacy)</CardTitle>
              <CardDescription>Последние выполненные серии Random Pulse</CardDescription>
            </CardHeader>
            <CardContent>
              <RandomPulseHistory />
            </CardContent>
          </Card>
        </div>
      )}


      {/* Grid Trading Tab */}
      {activeTab === 'gridTrading' && (
        <>
          {/* Status and Control Panel */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5" />
                    Grid Trading
                  </CardTitle>
                  <CardDescription>
                    Автоматическая торговля по сетке уровней цены
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  {seriesStatus.status === 'ACTIVE' ? (
                    <Button 
                      onClick={stopSeries}
                      variant="destructive"
                      className="flex items-center gap-2"
                    >
                      <Pause className="h-4 w-4" />
                      Остановить
                    </Button>
                  ) : (
                    <Button 
                      onClick={startSeries}
                      className="flex items-center gap-2"
                    >
                      <Play className="h-4 w-4" />
                      Запустить
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">Статус:</span>
                    <Badge className={seriesStatus.status === 'ACTIVE' ? 'bg-green-500' : 'bg-gray-400'}>
                      {seriesStatus.status === 'ACTIVE' ? 'Активна' : 'Остановлена'}
                    </Badge>
                  </div>
                  {seriesStatus.status === 'ACTIVE' && (
                    <>
                      <div className={`text-sm font-bold ${seriesStatus.profit_loss_usd >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        P&L: ${seriesStatus.profit_loss_usd?.toFixed(2) || '0.00'}
                      </div>
                      <div className="text-sm text-gray-600">
                        Покупок: {seriesStatus.total_buys || 0} | Продаж: {seriesStatus.total_sells || 0}
                      </div>
                    </>
                  )}
                </div>
              </div>
              
              {seriesStatus.status === 'ACTIVE' && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div>
                    <div className="text-gray-600">Базовая цена</div>
                    <div className="font-bold text-blue-600">${seriesStatus.base_price_usd?.toFixed(6) || '0.000000'}</div>
                  </div>
                  <div>
                    <div className="text-gray-600">Последняя продажа</div>
                    <div className="font-bold text-green-600">${seriesStatus.last_sell_price?.toFixed(6) || '0.000000'}</div>
                  </div>
                  <div>
                    <div className="text-gray-600">Последняя покупка</div>
                    <div className="font-bold text-red-600">${seriesStatus.last_buy_price?.toFixed(6) || '0.000000'}</div>
                  </div>
                  <div>
                    <div className="text-gray-600">Объем продаж</div>
                    <div className="font-bold">${seriesStatus.total_sell_volume_usd?.toFixed(2) || '0.00'}</div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Main Configuration */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Настройки Grid Trading
              </CardTitle>
              <CardDescription>
                Выберите режим торговли и настройте параметры
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Enable Grid Trading */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <label className="text-sm font-medium">Включить Grid Trading</label>
                  <p className="text-xs text-gray-500 mt-1">Активировать автоматическую торговлю</p>
                </div>
                <input
                  type="checkbox"
                  checked={config.enable_grid_trading}
                  onChange={(e) => setConfig({...config, enable_grid_trading: e.target.checked})}
                  className="h-5 w-5"
                />
              </div>

              {config.enable_grid_trading && (
                <>
                  {/* Trading Mode Selection */}
                  <div className="space-y-3">
                    <label className="text-sm font-medium block">Режим торговли</label>
                    <div className="grid grid-cols-3 gap-3">
                      <button
                        type="button"
                        onClick={() => setConfig({...config, grid_trading_mode: 'BOTH'})}
                        className={`p-4 border-2 rounded-lg transition-all ${
                          config.grid_trading_mode === 'BOTH'
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="font-semibold text-sm mb-1">Обе стороны</div>
                        <div className="text-xs text-gray-600">Покупка и продажа</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfig({...config, grid_trading_mode: 'BUY_ONLY'})}
                        className={`p-4 border-2 rounded-lg transition-all ${
                          config.grid_trading_mode === 'BUY_ONLY'
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="font-semibold text-sm mb-1">Только покупка</div>
                        <div className="text-xs text-gray-600">Только BUY операции</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfig({...config, grid_trading_mode: 'SELL_ONLY'})}
                        className={`p-4 border-2 rounded-lg transition-all ${
                          config.grid_trading_mode === 'SELL_ONLY'
                            ? 'border-red-500 bg-red-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="font-semibold text-sm mb-1">Только продажа</div>
                        <div className="text-xs text-gray-600">Только SELL операции</div>
                      </button>
                    </div>
                  </div>

                  {/* SELL_ONLY Mode Settings */}
                  {config.grid_trading_mode === 'SELL_ONLY' && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-sm font-medium">Продавать при покупке врага</label>
                          <p className="text-xs text-gray-600 mt-1">Реагировать на покупки врага</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={config.grid_sell_on_enemy_buy}
                          onChange={(e) => setConfig({...config, grid_sell_on_enemy_buy: e.target.checked})}
                          className="h-4 w-4"
                        />
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium block mb-2">
                          Процент от покупки врага (%)
                        </label>
                        <Input
                          type="number"
                          step="1"
                          min="1"
                          max="100"
                          value={config.grid_sell_percent_of_enemy}
                          onChange={(e) => setConfig({...config, grid_sell_percent_of_enemy: parseFloat(e.target.value)})}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Продавать {config.grid_sell_percent_of_enemy}% от объема каждой покупки врага
                        </p>
                      </div>
                      
                      {config.grid_sell_on_enemy_buy && (
                        <div>
                          <label className="text-sm font-medium block mb-2">
                            Минимальное изменение цены (%)
                          </label>
                          <Input
                            type="number"
                            step="0.1"
                            min="0.1"
                            value={config.grid_sell_price_change_percent}
                            onChange={(e) => setConfig({...config, grid_sell_price_change_percent: parseFloat(e.target.value)})}
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Продавать только если цена изменилась на {config.grid_sell_price_change_percent}% с момента последней продажи
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* BOTH Mode - Enemy Buy Reaction Settings */}
                  {config.grid_trading_mode === 'BOTH' && (
                    <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-sm font-medium">Продавать при покупке врага</label>
                          <p className="text-xs text-gray-600 mt-1">Дополнительно: реагировать на покупки врага</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={config.grid_sell_on_enemy_buy}
                          onChange={(e) => setConfig({...config, grid_sell_on_enemy_buy: e.target.checked})}
                          className="h-4 w-4"
                        />
                      </div>
                      
                      {config.grid_sell_on_enemy_buy && (
                        <>
                          <div>
                            <label className="text-sm font-medium block mb-2">
                              Процент от покупки врага (%)
                            </label>
                            <Input
                              type="number"
                              step="1"
                              min="1"
                              max="100"
                              value={config.grid_sell_percent_of_enemy}
                              onChange={(e) => setConfig({...config, grid_sell_percent_of_enemy: parseFloat(e.target.value)})}
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Продавать {config.grid_sell_percent_of_enemy}% от объема каждой покупки врага
                            </p>
                          </div>
                          
                          <div>
                            <label className="text-sm font-medium block mb-2">
                              Минимальное изменение цены (%)
                            </label>
                            <Input
                              type="number"
                              step="0.1"
                              min="0.1"
                              value={config.grid_sell_price_change_percent}
                              onChange={(e) => setConfig({...config, grid_sell_price_change_percent: parseFloat(e.target.value)})}
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Продавать только если цена изменилась на {config.grid_sell_price_change_percent}% с момента последней продажи
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* BOTH or BUY_ONLY Mode Settings */}
                  {(config.grid_trading_mode === 'BOTH' || config.grid_trading_mode === 'BUY_ONLY') && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg space-y-4">
                      <div>
                        <label className="text-sm font-medium block mb-2">
                          Интервал покупки (%)
                        </label>
                        <Input
                          type="number"
                          step="0.1"
                          value={config.grid_buy_interval_percent}
                          onChange={(e) => setConfig({...config, grid_buy_interval_percent: parseFloat(e.target.value)})}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Покупать каждые {config.grid_buy_interval_percent}% падения цены
                        </p>
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium block mb-2">
                          Сумма покупки ($)
                        </label>
                        <Input
                          type="number"
                          step="10"
                          value={config.grid_buy_amount_usd}
                          onChange={(e) => setConfig({...config, grid_buy_amount_usd: parseFloat(e.target.value)})}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Покупать на ${config.grid_buy_amount_usd} при каждом срабатывании
                        </p>
                      </div>
                    </div>
                  )}

                  {/* BOTH or SELL_ONLY Mode Settings (Grid-based selling) */}
                  {(config.grid_trading_mode === 'BOTH' || config.grid_trading_mode === 'SELL_ONLY') && !config.grid_sell_on_enemy_buy && (
                    <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg space-y-4">
                      <div>
                        <label className="text-sm font-medium block mb-2">
                          Интервал продажи (%)
                        </label>
                        <Input
                          type="number"
                          step="0.1"
                          value={config.grid_sell_interval_percent}
                          onChange={(e) => setConfig({...config, grid_sell_interval_percent: parseFloat(e.target.value)})}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Продавать каждые {config.grid_sell_interval_percent}% роста цены
                        </p>
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium block mb-2">
                          Процент от баланса (%)
                        </label>
                        <Input
                          type="number"
                          step="1"
                          value={config.grid_sell_amount_percent}
                          onChange={(e) => setConfig({...config, grid_sell_amount_percent: parseFloat(e.target.value)})}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Продавать {config.grid_sell_amount_percent}% от баланса {tokenSymbol || 'TOKEN'} при каждом срабатывании
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Min Transaction Size */}
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <label className="text-sm font-medium block mb-2">
                      Множитель минимальной транзакции
                    </label>
                    <Input
                      type="number"
                      step="1"
                      min="1"
                      value={config.min_transaction_multiplier}
                      onChange={(e) => setConfig({...config, min_transaction_multiplier: parseInt(e.target.value)})}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Минимальный размер = комиссия × {config.min_transaction_multiplier}
                    </p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Wallet Selection for Grid Trading */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Wallet Selection</CardTitle>
                  <CardDescription>
                    Choose which wallets to use for Grid Trading (empty = all wallets)
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={selectAllWallets}
                    variant="outline"
                    size="sm"
                  >
                    Select All
                  </Button>
                  <Button 
                    onClick={deselectAllWallets}
                    variant="outline"
                    size="sm"
                  >
                    Deselect All
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {availableWallets.length === 0 ? (
                <div className="text-center py-4 text-gray-500">
                  Loading wallets...
                </div>
              ) : (
                <>
                  {/* Quick Selection by Groups */}
                  <div className="mb-4">
                    <div className="text-xs font-medium text-gray-700 mb-2">Quick Select by Group:</div>
                    <div className="flex gap-2 flex-wrap">
                      {getWalletGroups().map(group => {
                        const formatNumber = (num) => Math.floor(num).toLocaleString('en-US');
                        return (
                          <button 
                            key={group.name}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              selectWalletsByGroup(group.name);
                            }}
                            className="border border-gray-300 rounded-lg px-3 py-2 hover:bg-gray-50 transition-colors text-left"
                          >
                            <div className="font-semibold text-sm">{group.name}</div>
                            <div className="text-[10px] text-gray-500 mt-1">
                              {group.count} wallets | TON: {formatNumber(group.totalTON)} | {tokenSymbol || 'TOKEN'}: {formatNumber(group.totalBUYT)}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Selection Summary */}
                  <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                    <div className="text-sm font-medium text-gray-700 mb-2">
                      Selected: {config.grid_trading_wallet_addresses?.length || 0} / {availableWallets.length} wallets
                      {(!config.grid_trading_wallet_addresses || config.grid_trading_wallet_addresses.length === 0) && (
                        <span className="ml-2 text-blue-600 font-medium">(All wallets will be used)</span>
                      )}
                    </div>
                    {config.grid_trading_wallet_addresses?.length > 0 && (() => {
                      const tokenAddress = config.token_address || globalTokenAddress;
                      const selectedWallets = availableWallets.filter(w => 
                        config.grid_trading_wallet_addresses.includes(w.address)
                      );
                      
                      const totalTON = selectedWallets.reduce((sum, w) => sum + (w.ton_balance || 0), 0) / 1e9;
                      const totalBUYT = selectedWallets.reduce((sum, w) => {
                        return sum + jettonReadable(w.jetton_balances, tokenAddress, globalTokenAddress);
                      }, 0);
                      
                      const formatNumber = (num) => Math.floor(num).toLocaleString('en-US');
                      
                      return (
                        <div className="flex gap-4 text-xs">
                          <div>
                            <span className="text-gray-600">Total TON:</span>
                            <span className="ml-1 font-bold text-gray-900">{formatNumber(totalTON)}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Total {tokenSymbol || 'TOKEN'}:</span>
                            <span className="ml-1 font-bold text-blue-600">{formatNumber(totalBUYT)}</span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-96 overflow-y-auto">
                    {walletSelectorEls}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Save Config Button */}
          <div className="flex justify-end">
            <Button 
              onClick={saveConfig}
              disabled={loading}
              className="flex items-center gap-2"
            >
              <Settings className="h-4 w-4" />
              {loading ? 'Saving...' : 'Save Configuration'}
            </Button>
          </div>

          {/* Series History */}
          {seriesStatus.status === 'ACTIVE' && seriesHistory.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Transaction History</CardTitle>
                <CardDescription>
                  Last {seriesHistory.length} transactions in current series
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Time</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Price (USD)</TableHead>
                        <TableHead>Amount ({tokenSymbol || 'TOKEN'})</TableHead>
                        <TableHead>Volume (USD)</TableHead>
                        <TableHead>Wallet</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {seriesHistory.map((tx) => (
                        <TableRow key={tx.id}>
                          <TableCell className="text-xs">
                            {new Date(tx.timestamp).toLocaleString()}
                          </TableCell>
                          <TableCell>
                            <Badge className={tx.transaction_type === 'BUY' ? 'bg-green-500' : 'bg-red-500'}>
                              {tx.transaction_type}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-mono">
                            ${tx.price_usd?.toFixed(6)}
                          </TableCell>
                          <TableCell className="font-mono">
                            {tx.amount_tokens?.toFixed(2)}
                          </TableCell>
                          <TableCell className="font-mono">
                            ${tx.amount_usd?.toFixed(2)}
                          </TableCell>
                          <TableCell className="text-xs font-mono">
                            {tx.wallet_address?.substring(0, 10)}...
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* PNL Analytics Tab */}
      {activeTab === 'avgPrices' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-green-600" />
                  PNL Аналитика
                </CardTitle>
                <CardDescription>
                  Полный анализ прибыли: Realized (от продаж) + Unrealized (в токенах)
                </CardDescription>
              </div>
              <div className="flex gap-2 items-center">
                <Button
                  onClick={recalculateAveragePrice}
                  disabled={recalculating || pnlLoading}
                  variant="default"
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {recalculating ? 'Пересчет...' : 'Пересчитать среднюю цену'}
                </Button>
                {recalcStatus && (
                  <div className="text-sm text-gray-600">
                    {recalcStatus.status === 'running' && (
                      <span>
                        Прогресс: {recalcStatus.current || 0} / {recalcStatus.total || 0} кошельков
                        {recalcStatus.current_wallet && (
                          <span className="ml-2 text-xs">({recalcStatus.current_wallet.slice(0, 8)}...)</span>
                        )}
                      </span>
                    )}
                    {recalcStatus.status === 'completed' && (
                      <span className="text-green-600">
                        Завершено: {recalcStatus.swaps_added || 0} операций добавлено
                      </span>
                    )}
                    {recalcStatus.status === 'failed' && (
                      <span className="text-red-600">
                        Ошибка: {recalcStatus.error || 'Неизвестная ошибка'}
                      </span>
                    )}
                  </div>
                )}
                <Button
                  onClick={() => { loadPnlAnalytics(); loadAvgBuyPrices(); loadWallets(); }}
                  disabled={pnlLoading || recalculating}
                  variant="outline"
                >
                  {pnlLoading ? 'Загрузка...' : 'Обновить'}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {pnlLoading ? (
              <div className="text-center py-8 text-gray-500">
                <p>Загрузка PNL аналитики...</p>
              </div>
            ) : !pnlData?.totals ? (
              <div className="text-center py-8 text-gray-500">
                <p>Нет данных. Нажмите "Обновить" для загрузки.</p>
              </div>
            ) : (
              <>
                {/* Current Price */}
                <div className="flex items-center justify-between p-3 bg-gray-100 rounded-lg mb-4">
                  <span className="text-sm text-gray-600">Текущая цена токена:</span>
                  <span className="font-bold text-lg">
                    ${pnlData.current_token_price?.toFixed(8) || '0'}
                  </span>
                </div>

                {/* Main PNL Summary */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  {/* Realized PNL */}
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="text-sm font-medium text-blue-700 mb-2">📈 Realized PNL</h4>
                    <p className="text-xs text-blue-600 mb-2">Зафиксированная прибыль от продаж</p>
                    <p className={`text-2xl font-bold ${pnlData.totals.realized_pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {pnlData.totals.realized_pnl >= 0 ? '+' : ''}${pnlData.totals.realized_pnl?.toFixed(2) || '0'}
                    </p>
                    <div className="mt-2 text-xs text-blue-600 space-y-1">
                      <div className="flex justify-between">
                        <span>Выручка от продаж:</span>
                        <span className="font-medium">${pnlData.totals.total_proceeds_usd?.toFixed(2) || '0'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Себестоимость:</span>
                        <span className="font-medium">${pnlData.totals.cost_of_goods_sold?.toFixed(2) || '0'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Unrealized PNL */}
                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <h4 className="text-sm font-medium text-purple-700 mb-2">📊 Unrealized PNL</h4>
                    <p className="text-xs text-purple-600 mb-2">Потенциальная прибыль в токенах</p>
                    <p className={`text-2xl font-bold ${pnlData.totals.unrealized_pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {pnlData.totals.unrealized_pnl >= 0 ? '+' : ''}${pnlData.totals.unrealized_pnl?.toFixed(2) || '0'}
                    </p>
                    <div className="mt-2 text-xs text-purple-600 space-y-1">
                      <div className="flex justify-between">
                        <span>Текущая стоимость:</span>
                        <span className="font-medium">${pnlData.totals.current_holdings_value?.toFixed(2) || '0'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Токенов на руках:</span>
                        <span className="font-medium">{pnlData.totals.current_holdings?.toLocaleString(undefined, {maximumFractionDigits: 0}) || '0'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Total PNL */}
                  <div className={`p-4 rounded-lg border ${pnlData.totals.total_pnl >= 0 ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'}`}>
                    <h4 className={`text-sm font-medium mb-2 ${pnlData.totals.total_pnl >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      🎯 Total PNL
                    </h4>
                    <p className={`text-xs mb-2 ${pnlData.totals.total_pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      Общий результат
                    </p>
                    <p className={`text-3xl font-bold ${pnlData.totals.total_pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {pnlData.totals.total_pnl >= 0 ? '+' : ''}${pnlData.totals.total_pnl?.toFixed(2) || '0'}
                    </p>
                    <div className={`mt-2 text-xs space-y-1 ${pnlData.totals.total_pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      <div className="flex justify-between">
                        <span>ROI:</span>
                        <span className="font-bold">{pnlData.totals.roi_percent >= 0 ? '+' : ''}{pnlData.totals.roi_percent?.toFixed(1) || '0'}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Break-even:</span>
                        <span className="font-medium">${pnlData.totals.break_even_price?.toFixed(8) || '0'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Trading Stats */}
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 mb-6">
                  <h4 className="font-medium text-amber-800 mb-3">📊 Статистика торговли</h4>
                  <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-sm">
                    <div>
                      <p className="text-amber-600">Потрачено</p>
                      <p className="font-bold text-lg">${pnlData.totals.total_spent_usd?.toFixed(2) || '0'}</p>
                    </div>
                    <div>
                      <p className="text-amber-600">Получено</p>
                      <p className="font-bold text-lg">${pnlData.totals.total_proceeds_usd?.toFixed(2) || '0'}</p>
                    </div>
                    <div>
                      <p className="text-amber-600">Куплено токенов</p>
                      <p className="font-bold text-lg">{pnlData.totals.total_bought?.toLocaleString(undefined, {maximumFractionDigits: 0}) || '0'}</p>
                    </div>
                    <div>
                      <p className="text-amber-600">Продано токенов</p>
                      <p className="font-bold text-lg">{pnlData.totals.total_sold?.toLocaleString(undefined, {maximumFractionDigits: 0}) || '0'}</p>
                    </div>
                    <div>
                      <p className="text-amber-600">Ср. цена покупки</p>
                      <p className="font-bold text-lg">${pnlData.totals.avg_buy_price?.toFixed(8) || '0'}</p>
                    </div>
                    <div>
                      <p className="text-amber-600">Ср. цена продажи</p>
                      <p className="font-bold text-lg">${pnlData.totals.avg_sell_price?.toFixed(8) || '0'}</p>
                    </div>
                  </div>
                </div>

                {/* Per Wallet Table */}
                {pnlData.per_wallet && Object.keys(pnlData.per_wallet).length > 0 && (
                  <div className="overflow-x-auto">
                    <h4 className="font-medium text-gray-800 mb-3">По кошелькам ({pnlData.totals.wallets_with_data})</h4>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Кошелек</TableHead>
                          <TableHead className="text-right">Ср. покупки</TableHead>
                          <TableHead className="text-right">Ср. продажи</TableHead>
                          <TableHead className="text-right">Куплено</TableHead>
                          <TableHead className="text-right">Продано</TableHead>
                          <TableHead className="text-right">Баланс</TableHead>
                          <TableHead className="text-right">Realized</TableHead>
                          <TableHead className="text-right">Unrealized</TableHead>
                          <TableHead className="text-right">Total PNL</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {Object.entries(pnlData.per_wallet)
                          .sort((a, b) => Math.abs(b[1].total_pnl) - Math.abs(a[1].total_pnl))
                          .map(([address, data]) => {
                            // Get current balance from availableWallets
                            const wallet = availableWallets.find(w => w.address === address);
                            const tokenAddress = config.token_address || globalTokenAddress;
                            const currentBalance = jettonReadable(wallet?.jetton_balances, tokenAddress, globalTokenAddress);
                            
                            return (
                              <TableRow key={address}>
                                <TableCell className="font-mono text-xs">
                                  <a 
                                    href={`https://tonviewer.com/${address}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:underline"
                                  >
                                    {address.slice(0, 8)}...{address.slice(-6)}
                                  </a>
                                </TableCell>
                                <TableCell className="text-right text-xs">
                                  ${data.avg_buy_price?.toFixed(8) || '0'}
                                </TableCell>
                                <TableCell className="text-right text-xs">
                                  {data.avg_sell_price > 0 ? `$${data.avg_sell_price.toFixed(8)}` : '—'}
                                </TableCell>
                                <TableCell className="text-right text-xs text-gray-600">
                                  {data.total_bought?.toLocaleString(undefined, {maximumFractionDigits: 0}) || '0'}
                                </TableCell>
                                <TableCell className="text-right text-xs text-gray-600">
                                  {data.total_sold?.toLocaleString(undefined, {maximumFractionDigits: 0}) || '0'}
                                </TableCell>
                                <TableCell className="text-right text-xs font-medium">
                                  {currentBalance > 0 
                                    ? currentBalance.toLocaleString(undefined, {maximumFractionDigits: 0})
                                    : <span className="text-gray-400">0</span>
                                  }
                                </TableCell>
                                <TableCell className={`text-right text-xs font-medium ${data.realized_pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {data.realized_pnl !== 0 ? (
                                    <>{data.realized_pnl >= 0 ? '+' : ''}${data.realized_pnl.toFixed(2)}</>
                                  ) : '—'}
                                </TableCell>
                                <TableCell className={`text-right text-xs font-medium ${data.unrealized_pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {data.unrealized_pnl !== 0 ? (
                                    <>{data.unrealized_pnl >= 0 ? '+' : ''}${data.unrealized_pnl.toFixed(2)}</>
                                  ) : '—'}
                                </TableCell>
                                <TableCell className={`text-right text-xs font-bold ${data.total_pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {data.total_pnl >= 0 ? '+' : ''}${data.total_pnl.toFixed(2)}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Logs Tab */}
      {activeTab === 'logs' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  System Logs
                </CardTitle>
                <CardDescription>
                  Real-time Market Maker operation logs
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={loadLogs}
                  variant="outline"
                  size="sm"
                  disabled={loading}
                >
                  Refresh
                </Button>
                {status.running ? (
                  <Badge className="bg-green-600">
                    <Activity className="h-3 w-3 mr-1 animate-pulse" />
                    Running
                  </Badge>
                ) : (
                  <Badge variant="secondary">Stopped</Badge>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {!status.running && (
              <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  ⚠️ <strong>Market Maker is STOPPED</strong> - Strategy is not monitoring swaps.
                  Нажмите «Запустить питание» в пульте управления сверху страницы.
                </p>
              </div>
            )}
            
            <div className="mt-4 space-y-6">
              <div>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-200 uppercase tracking-wide">
                      Все события
                    </h3>
                    <p className="text-xs text-gray-500">
                      Последние {categorizedLogs.all.length} записей в порядке убывания времени
                    </p>
                  </div>
                  <span className="text-xs text-gray-500">
                    Обновление каждые 3 сек • лимит {LOG_FETCH_LIMIT}
                  </span>
                </div>
                <div className="mt-3">
                  {renderAllLogsTable(categorizedLogs.all)}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-200 uppercase tracking-wide">
                  Категории
                </h3>
                <p className="text-xs text-gray-500">
                  Отдельные списки для быстрого просмотра результатов, ошибок и служебных событий
                </p>
                <div className="mt-3 grid gap-4 md:grid-cols-3">
                  {renderCategoryPanel(
                    'Выполненные действия',
                    'Успешно завершённые операции и реакции',
                    categorizedLogs.actions,
                    'text-emerald-400'
                  )}
                  {renderCategoryPanel(
                    'Ошибки',
                    'Проблемы, требующие внимания',
                    categorizedLogs.errors,
                    'text-red-400'
                  )}
                  {renderCategoryPanel(
                    'Информативные',
                    'Информационные и предупредительные сообщения',
                    categorizedLogs.info,
                    'text-blue-400'
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Strategy Form Component
const StrategyForm = ({ strategy, walletGroups, onSubmit, onCancel, defaultTokenAddress, tokenSymbol }) => {
  // Helper function to get initial form data from strategy
  const getInitialFormData = (strat, tokenAddr) => ({
    name: strat?.name ?? 'New Strategy',
    enabled: strat?.enabled ?? false,
    wallet_group_name: strat?.wallet_group_name ?? '',
    token_address: strat?.token_address ?? tokenAddr,
    min_enemy_volume_usd: strat?.min_enemy_volume_usd ?? 10.0,
    reaction_delay_seconds: strat?.reaction_delay_seconds ?? 5.0,
    enemy_buy_sell_min_percent: strat?.enemy_buy_sell_min_percent ?? 50.0,
    enemy_buy_sell_max_percent: strat?.enemy_buy_sell_max_percent ?? 100.0,
    enemy_sell_buy_min_percent: strat?.enemy_sell_buy_min_percent ?? 50.0,
    enemy_sell_buy_max_percent: strat?.enemy_sell_buy_max_percent ?? 100.0,
    split_threshold_usd: strat?.split_threshold_usd ?? 50.0,
    max_per_operation_usd: strat?.max_per_operation_usd ?? 1000.0,
    enable_price_filter: strat?.enable_price_filter ?? false,
    min_sell_price_increase_percent: strat?.min_sell_price_increase_percent ?? 2.0,
    min_buy_price_decrease_percent: strat?.min_buy_price_decrease_percent ?? 2.0,
    // Average Cost Protection
    enable_avg_cost_protection: strat?.enable_avg_cost_protection ?? false,
    min_profit_margin_percent: strat?.min_profit_margin_percent ?? 5.0,
    min_sell_price_usd: strat?.min_sell_price_usd ?? 0,
    // Max Buy Price Protection
    max_buy_price_usd: strat?.max_buy_price_usd ?? 0.0,
  });

  const [formData, setFormData] = useState(() => getInitialFormData(strategy, defaultTokenAddress));

  // Update form data when strategy prop changes (e.g., switching from edit to new or vice versa)
  useEffect(() => {
    setFormData(getInitialFormData(strategy, defaultTokenAddress));
  }, [strategy, defaultTokenAddress]);

  // Живая средняя цена закупки группы — показываем при включённой защите
  const [avgInfo, setAvgInfo] = useState(null);
  useEffect(() => {
    if (!formData.enable_avg_cost_protection || !formData.wallet_group_name) { setAvgInfo(null); return; }
    let cancelled = false;
    axios.get(`${API_URL}/api/market-maker/avg-buy-price`, { params: { group_name: formData.wallet_group_name, margin_percent: formData.min_profit_margin_percent || 0 } })
      .then(r => { if (!cancelled) setAvgInfo(r.data); })
      .catch(() => { if (!cancelled) setAvgInfo(null); });
    return () => { cancelled = true; };
  }, [formData.enable_avg_cost_protection, formData.wallet_group_name, formData.min_profit_margin_percent]);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Санитизация: пустой числовой инпут даёт NaN → в JSON это null → Pydantic float падает 422.
    // Любое NaN/'' числовое поле заменяем на дефолт модели.
    const defaults = getInitialFormData(null, defaultTokenAddress);
    const clean = { ...formData };
    for (const k of Object.keys(clean)) {
      const isNumDefault = typeof defaults[k] === 'number';
      if (isNumDefault && (clean[k] === '' || clean[k] === null || clean[k] === undefined || (typeof clean[k] === 'number' && isNaN(clean[k])))) {
        clean[k] = defaults[k];
      }
    }
    onSubmit(clean);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Strategy Name */}
      <div className="space-y-2">
        <label className="text-sm font-medium">Название стратегии</label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({...formData, name: e.target.value})}
          required
        />
      </div>

      {/* Wallet Group */}
      <div className="space-y-2">
        <label className="text-sm font-medium flex items-center gap-2">
          <Users className="h-4 w-4" />
          Группа кошельков
        </label>
        <select
          className="w-full px-3 py-2 border rounded-md"
          value={formData.wallet_group_name || ''}
          onChange={(e) => setFormData({...formData, wallet_group_name: e.target.value || null})}
        >
          <option value="">Все кошельки</option>
          {walletGroups.length === 0 ? (
            <option disabled>Загрузка групп...</option>
          ) : (
            walletGroups.map((group) => {
              const groupName = typeof group === 'string' ? group : group.group_name;
              const groupData = typeof group === 'object' ? group : null;
              return (
                <option key={groupName} value={groupName}>
                  {groupName}
                  {groupData && (
                    ` (${groupData.count} кошельков, ${groupData.total_ton_readable?.toFixed(2) || '0.00'} TON, ${groupData.total_buyt_readable?.toFixed(2) || '0.00'} ${tokenSymbol || 'TOKEN'})`
                  )}
                </option>
              );
            })
          )}
        </select>
        <p className="text-xs text-gray-500">Выберите группу кошельков или оставьте пустым для всех</p>
        {/* Show detailed info for selected group */}
        {formData.wallet_group_name && walletGroups.find(g => (typeof g === 'object' ? g.group_name : g) === formData.wallet_group_name) && (() => {
          const selectedGroup = walletGroups.find(g => (typeof g === 'object' ? g.group_name : g) === formData.wallet_group_name);
          if (typeof selectedGroup === 'object') {
            return (
              <div className="p-3 bg-blue-50 rounded-md text-sm">
                <p className="font-medium mb-1">Информация о группе:</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-gray-600">Кошельков:</span>
                    <span className="ml-2 font-medium">{selectedGroup.count}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">TON баланс:</span>
                    <span className="ml-2 font-medium">{selectedGroup.total_ton_readable?.toFixed(2) || '0.00'} TON</span>
                  </div>
                  <div>
                    <span className="text-gray-600">{tokenSymbol || 'TOKEN'} баланс:</span>
                    <span className="ml-2 font-medium">{selectedGroup.total_buyt_readable?.toFixed(2) || '0.00'} {tokenSymbol || 'TOKEN'}</span>
                  </div>
                </div>
              </div>
            );
          }
          return null;
        })()}
      </div>

      {/* Enabled */}
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="enabled"
          checked={formData.enabled}
          onChange={(e) => setFormData({...formData, enabled: e.target.checked})}
          className="h-4 w-4"
        />
        <label htmlFor="enabled" className="text-sm font-medium">Включить стратегию</label>
      </div>

      {/* Min Enemy Volume */}
      <div className="space-y-2">
        <label className="text-sm font-medium flex items-center gap-2">
          <DollarSign className="h-4 w-4" />
          Минимальный объем врага (USD)
        </label>
        <Input
          type="number"
          value={formData.min_enemy_volume_usd}
          onChange={(e) => setFormData({...formData, min_enemy_volume_usd: parseFloat(e.target.value)})}
          step="1"
          min="1"
          required
        />
      </div>

      {/* Reaction Delay */}
      <div className="space-y-2">
        <label className="text-sm font-medium flex items-center gap-2">
          <Clock className="h-4 w-4" />
          Задержка реакции (секунды)
        </label>
        <Input
          type="number"
          value={formData.reaction_delay_seconds}
          onChange={(e) => setFormData({...formData, reaction_delay_seconds: parseFloat(e.target.value)})}
          step="0.5"
          min="0"
          required
        />
      </div>

      {/* Enemy BUY -> We SELL */}
      <div className="p-4 bg-red-50 rounded-lg space-y-3">
        <h3 className="font-medium flex items-center gap-2">
          <TrendingDown className="h-4 w-4 text-red-600" />
          Когда враг ПОКУПАЕТ → Мы ПРОДАЕМ
        </h3>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <label className="text-sm">Min % продажи</label>
            <Input
              type="number"
              value={formData.enemy_buy_sell_min_percent}
              onChange={(e) => setFormData({...formData, enemy_buy_sell_min_percent: parseFloat(e.target.value)})}
              step="any"
              min="0"
              max="100"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm">Max % продажи</label>
            <Input
              type="number"
              value={formData.enemy_buy_sell_max_percent}
              onChange={(e) => setFormData({...formData, enemy_buy_sell_max_percent: parseFloat(e.target.value)})}
              step="any"
              min="0"
              max="200"
            />
          </div>
        </div>
      </div>

      {/* Enemy SELL -> We BUY */}
      <div className="p-4 bg-green-50 rounded-lg space-y-3">
        <h3 className="font-medium flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-green-600" />
          Когда враг ПРОДАЕТ → Мы ПОКУПАЕМ
        </h3>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <label className="text-sm">Min % покупки</label>
            <Input
              type="number"
              value={formData.enemy_sell_buy_min_percent}
              onChange={(e) => setFormData({...formData, enemy_sell_buy_min_percent: parseFloat(e.target.value)})}
              step="any"
              min="0"
              max="100"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm">Max % покупки</label>
            <Input
              type="number"
              value={formData.enemy_sell_buy_max_percent}
              onChange={(e) => setFormData({...formData, enemy_sell_buy_max_percent: parseFloat(e.target.value)})}
              step="any"
              min="0"
              max="200"
            />
          </div>
        </div>
      </div>

      {/* Average Cost Protection */}
      <div className="p-4 bg-amber-50 rounded-lg space-y-3 border border-amber-200">
        <div className="flex items-center justify-between">
          <h3 className="font-medium flex items-center gap-2 text-amber-800">
            <Target className="h-4 w-4" />
            Защита от продажи ниже средней цены
          </h3>
          <input
            type="checkbox"
            id="enable_avg_cost_protection"
            checked={formData.enable_avg_cost_protection}
            onChange={(e) => setFormData({...formData, enable_avg_cost_protection: e.target.checked})}
            className="h-4 w-4"
          />
        </div>
        <p className="text-xs text-amber-700">
          Когда включено, кошелек НЕ будет продавать токены, если текущая цена ниже средней цены покупки + заданный зазор. Это защищает от продажи в убыток.
        </p>
        {formData.enable_avg_cost_protection && (
          <div className="space-y-3 mt-3 pt-3 border-t border-amber-200">
            {!formData.wallet_group_name ? (
              <div className="bg-white p-3 rounded-md border border-amber-300 text-xs text-gray-500">Выберите группу кошельков выше — покажу её среднюю цену закупки.</div>
            ) : avgInfo && (
              <div className="bg-white p-3 rounded-md border border-amber-300 text-xs space-y-1">
                {avgInfo.wallets_with_history > 0 ? (
                  <>
                    <div className="flex justify-between"><span className="text-gray-600">Средняя цена закупки группы:</span><span className="font-bold text-amber-800">${Number(avgInfo.avg_buy_price_usd).toFixed(8)}</span></div>
                    <div className="flex justify-between"><span className="text-gray-600">Текущая цена:</span><span className="font-medium">${Number(avgInfo.current_price_usd).toFixed(8)}</span></div>
                    <div className="flex justify-between"><span className="text-gray-600">Мин. цена продажи (+{avgInfo.margin_percent}%):</span><span className="font-bold">${Number(avgInfo.min_sell_price_usd).toFixed(8)}</span></div>
                    <div className={`flex justify-between font-semibold ${avgInfo.sell_allowed ? 'text-green-600' : 'text-red-600'}`}><span>Сейчас продажа:</span><span>{avgInfo.sell_allowed ? '✅ разрешена' : '🛡️ заблокирована'}</span></div>
                    <div className="text-gray-400 pt-1">Кошельков с историей покупок: {avgInfo.wallets_with_history}/{avgInfo.wallets_total} • обновляется live</div>
                  </>
                ) : (
                  <div className="text-gray-500">В группе «{avgInfo.group_name}» нет истории покупок — средняя не определена, кошельки смогут продавать свободно.</div>
                )}
              </div>
            )}
            <div className="space-y-2">
              <label className="text-sm font-medium text-amber-800">Минимальный зазор над средней ценой (%)</label>
              <Input
                type="number"
                value={formData.min_profit_margin_percent}
                onChange={(e) => { const v = parseFloat(e.target.value); setFormData({...formData, min_profit_margin_percent: isNaN(v) ? 0 : v}); }}
                step="any"
                min="0"
                className="bg-white"
              />
              <p className="text-xs text-amber-600">
                Зазор в ПРОЦЕНТАХ. Пример: средняя цена $0.0001 и зазор 5% → продажа только при цене выше $0.000105
              </p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-amber-800">Минимальная цена продажи ($, абсолютная) — опционально</label>
              <Input
                type="number"
                value={formData.min_sell_price_usd ?? 0}
                onChange={(e) => { const v = parseFloat(e.target.value); setFormData({...formData, min_sell_price_usd: isNaN(v) ? 0 : v}); }}
                step="any"
                min="0"
                placeholder="напр. 0.00019 — не продавать дешевле этой цены"
                className="bg-white"
              />
              <p className="text-xs text-amber-600">
                Жёсткий пол цены: если задано (&gt; 0), кошелёк НЕ продаёт, пока текущая цена ниже этого значения — независимо от средней цены. 0 = выключено.
              </p>
            </div>
            <div className="bg-amber-100/50 p-3 rounded-md text-xs text-amber-800">
              <p className="font-medium mb-1">Как это работает:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Система рассчитывает среднюю цену покупки для каждого кошелька</li>
                <li>Перед продажей проверяет: текущая цена >= средняя + зазор%</li>
                <li>Если цена ниже - кошелек пропускается, продажа блокируется</li>
                <li>Кошельки без истории покупок могут продавать свободно</li>
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Max Buy Price Protection */}
      <div className="p-4 bg-red-50 rounded-lg space-y-3 border border-red-200">
        <div className="flex items-center justify-between">
          <h3 className="font-medium flex items-center gap-2 text-red-800">
            <ShieldAlert className="h-4 w-4" />
            Защита от покупки выше максимальной цены
          </h3>
        </div>
        <p className="text-xs text-red-700">
          Когда задано значение больше 0, стратегия НЕ будет покупать токены, если текущая цена превышает указанный лимит. Это защищает от покупок на пиках.
        </p>
        <div className="space-y-2">
          <label className="text-sm font-medium text-red-800">Максимальная цена покупки (USD)</label>
          <Input
            type="number"
            value={formData.max_buy_price_usd}
            onChange={(e) => setFormData({...formData, max_buy_price_usd: parseFloat(e.target.value) || 0})}
            step="0.0000001"
            min="0"
            placeholder="0 = без ограничения"
            className="bg-white"
          />
          <p className="text-xs text-red-600">
            Пример: если указать 0.0001, то покупки будут заблокированы при цене выше $0.0001. Укажите 0 чтобы отключить ограничение.
          </p>
        </div>
        <div className="bg-red-100/50 p-3 rounded-md text-xs text-red-800">
          <p className="font-medium mb-1">Как это работает:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Перед каждой покупкой система проверяет текущую цену токена</li>
            <li>Если цена превышает максимум - ВСЕ покупки блокируются</li>
            <li>Продажи НЕ блокируются и работают в обычном режиме</li>
            <li>Значение 0 = защита отключена, покупки без ограничений</li>
          </ul>
        </div>
      </div>

      {/* Distribution Settings */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium flex items-center gap-2">
            <Target className="h-4 w-4" />
            Порог разбиения (USD)
          </label>
          <Input
            type="number"
            value={formData.split_threshold_usd}
            onChange={(e) => {
              const v = parseFloat(e.target.value);
              setFormData({...formData, split_threshold_usd: Number.isNaN(v) ? 0 : v});
            }}
            step="any"
            min="1"
            required
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Макс. на операцию (USD)
          </label>
          <Input
            type="number"
            value={formData.max_per_operation_usd}
            onChange={(e) => {
              const v = parseFloat(e.target.value);
              setFormData({...formData, max_per_operation_usd: Number.isNaN(v) ? 0 : v});
            }}
            step="any"
            min="10"
            required
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
          Отмена
        </Button>
        <Button type="submit" className="flex-1">
          {strategy ? "Сохранить" : "Создать"}
        </Button>
      </div>
    </form>
  );
};

export default MarketMaker;
